import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { useHistory, useParams } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite/no-important';
import {
  Toast,
  TabsContainer,
  TabItem,
  PopupMenu,
  TextInput,
  Button,
  DialogBox,
  Checkbox,
  TimePicker
} from 'quantux-ui';
import CaseScheduleContext from './case-schedule-context';
import CaseDoc from '../case-doc';
import CaseTab from './case-tab';
import PatientTab from './patient-tab';
import PayerTab from './payer-tab';
import CaseField from '../revenue-cycle-management/case-field';
import CaseUserCard from '../revenue-cycle-management/case-user-card';
import CriticalWarningIcon from '../svg-icons/critical-warning-icon';
import PatientCard from '../patient-card';
import IntraOp from './intraop';
import { blue, gray, salmon, yellow } from '../../theme';
import { FLAG_VALUE, BASE_URL, findFlag, padZero, getHighlightedText } from '../../utils';
import SuccessIcon from '../svg-icons/success-icon';

// #region ICONS
const IS_NONE_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none"><path fill="#96C8FF" fillRule="evenodd" d="M8 0C3.584 0 0 3.584 0 8s3.584 8 8 8 8-3.584 8-8-3.584-8-8-8ZM1.714 8.029c0-3.452 2.855-6.248 6.379-6.248 1.475 0 2.83.492 3.907 1.32l-8.938 8.754A6.091 6.091 0 0 1 1.714 8.03ZM4 12.904a6.385 6.385 0 0 0 3.907 1.32c3.524 0 6.379-2.796 6.379-6.247a6.091 6.091 0 0 0-1.348-3.827L4 12.904Z" clipRule="evenodd" /></svg>
);

const FLAG_ICON = (
  <svg
    width="12"
    height="12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.998 0H0v12h12V0h-.002Zm0 0v12L7.78 6l4.217-6Z"
      fill="#FF6060"
    />
  </svg>
);

const CRITIAL_ICON = (
  <svg
    width="16"
    height="13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 12.364 8 0 0 12.364h16Zm-7.273-2.182H7.273V8.728h1.454v1.454ZM7.273 8h1.454V5.091H7.273v2.91Z"
      fill="#FF0"
    />
  </svg>
);

const BELL_ICON = (
  <svg
    width="16"
    height="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14 7.439v4.376l2 1.75v.875H9.988C9.878 15.314 9.026 16 8 16c-1.036 0-1.88-.686-1.988-1.56H0v-.875l2-1.75V7.439c0-2.696 1.63-4.936 4.5-5.531v-.595C6.5.586 7.17 0 8 0s1.5.586 1.5 1.313v.595c2.86.595 4.5 2.844 4.5 5.53Z"
      fill="#FF6060"
    />
  </svg>
);

const SUCCESS_ICON = (
  <svg
    width="12"
    height="12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 6a6 6 0 1 1 12 0A6 6 0 1 1 0 6Zm2.646-.238 1.896 1.796L9.36 3l.641.608-5.458 5.17L2 6.369l.646-.607Z"
      fill="#00B080"
    />
  </svg>
);

const endIcon = (
  <svg
    width="16"
    height="12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 11.196 12.659 6v6h2.893V0H12.66v6L0 .804v10.392Z"
      fill="#96C8FF"
    />
  </svg>
);
// #endregion

const MOBILE_BREAK_POINT = 1012; // was 821 before
const DRAWER_WIDTH = 355;
const DIALOG_WIDTH = window.innerWidth <= 576 ? (window.innerWidth - 16) : 350;
const COMMON_TAB_ITEM_STYLES = {
  height: '30px',
  borderBottom: 'none',
  marginRight: '0'
};

const TABS = [
  {
    id: 'faceSheet_tab',
    pKey: '1111',
    isIconText: false,
    text: 'FaceSheet',
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%' }
  },
  {
    id: 'records_tab',
    pKey: '1331',
    isIconText: false,
    text: 'Records',
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%' }
  },
  {
    id: 'consents_tab',
    pKey: '1112',
    isIconText: false,
    text: 'Consents',
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', boderLeft: '1px solid black' }
  },
  {
    id: 'nursing_tab',
    pKey: '1113',
    isIconText: false,
    text: 'Nursing',
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  },
  {
    id: 'surgeon_tab',
    pKey: '1114',
    isIconText: false,
    text: 'Surgeon',
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  },
  {
    id: 'anesthesia_tab',
    pKey: '1115',
    isIconText: false,
    text: 'Anesthesia',
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  },
  {
    id: 'orders_tab',
    pKey: '8899',
    isIconText: false,
    text: 'Orders',
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  },
  {
    id: 'discharge_tab',
    pKey: '1116',
    isIconText: false,
    text: 'Discharge',
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  }
];

const TABS_TEXT_STYLES = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '12px'
};

const CaseDashboard = (props) => {
  const [showSavedMsg, setShowSavedMsg] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [user, setUser] = useState();
  const [masterFlag, setMasterFlag] = useState(FLAG_VALUE.critical);
  const [scheduleMasterFlag, setScheduleMasterFlag] = useState(FLAG_VALUE.critical);
  const [isMobileLayout, setIsMobileLayout] = useState(window.innerWidth < MOBILE_BREAK_POINT);
  const [showScheduleDrawer, setShowScheduleDrawer] = useState(false);
  const [showPacuMenu, setShowPacuMenu] = useState(false);

  // --- Case Tab Data
  const [caseData, setCaseData] = useState({});

  // --- Case Docs Tabs
  const [activeTab, setActiveTab] = useState(TABS[0].text);
  const [consentActiveTab, setConsentActiveTab] = useState('anesthesia');
  const [anesthesiaSubTab, setAnesthesiaSubTab] = useState('preOp');
  const [recordsSubTab, setRecordsSubTab] = useState('labs');
  const [ordersSubTab, setOrdersSubTab] = useState('preOp');

  // --- Patient Card
  const [patientDos, setPatientDos] = useState('');
  const [patientAdmType, setPatientAdmType] = useState('');
  const [patientFacility, setPatientFacility] = useState('');
  const [patientSurgeons, setPatientSurgeons] = useState([]);
  const [patientAnesthesia, setPatientAnesthesia] = useState([]);
  const [patientPreOpDx, setPatientPreOpDx] = useState([]);
  const [patientSurgicalProcedures, setPatientSurgicalProcedures] = useState([]);

  // --- Schedule Forms Drawer
  const [drawerActiveTab, setDrawerActiveTab] = useState('case');
  const [caseTabIcon, setCaseTabIcon] = useState(CRITIAL_ICON);
  const [caseFlag, setCaseFlag] = useState(FLAG_VALUE.critical);
  const [patientFlag, setPatientFlag] = useState(FLAG_VALUE.warning);
  const [payerFlag, setPayerFlag] = useState(FLAG_VALUE.warning);

  // Patient handover
  const [patientHandover, setPatientHandover] = useState('PACU');

  // --- Patient Tab & Data
  const [patientData, setPatientData] = useState({});

  // -- Case Docs (NEW)
  const [faceSheetDocs, setFaceSheetDocs] = useState([]);
  const [isNoneFaceSheet, setIsNoneFaceSheet] = useState(false);
  const [faceSheetFormType, setFaceSheetFormType] = useState('Face Sheet');
  // Consent -- Anesthesia
  const [consentFormType, setConsentFormType] = useState('Anesthesia Consent Form');
  const [consentFormDocs, setConsentFormDocs] = useState([]);
  const [isNoneConsentFormDocs, setIsNoneConsentFormDocs] = useState(false);
  // Consent - Surgery
  const [consentSurgFormType, setConsentSurgFormType] = useState('Surgery Consent Form');
  const [consentSurgFormDocs, setConsentSurgFormDocs] = useState([]);
  const [isNoneConsentSurgFormDocs, setIsNoneConsentSurgFormDocs] = useState(true);
  // -- Anesthesia sub tabs
  const [preOpFormType, setPreOpFormType] = useState('PreOp Form');
  const [preOpFormDocs, setPreOpFormDocs] = useState([]);
  const [isNonePreOpFormDocs, setIsNonePreOpFormDocs] = useState(false);
  const [intraOpFormType, setIntraOpFormType] = useState('Intra Op Form');
  const [intraOpFormDocs, setIntraOpFormDocs] = useState([]);
  const [isNoneIntraOpFormDocs, setIsNoneIntraOpFormDocs] = useState(false);
  const [postOpFormType, setPostOpFormType] = useState('PostOp Form');
  const [postOpFormDocs, setPostOpFormDocs] = useState([]);
  const [isNonePostOpFormDocs, setIsNonePostOpFormDocs] = useState(false);
  // --- New Docs
  // Anesthesia Procedures
  const [anesthesiaProcedureValue, setAnesthesiaProcedureValue] = useState('');
  const [showAnesthesiaProceduresSearchMenu, setShowAnesthesiaProceduresSearchMenu] = useState(false);
  const [anesthesiaProceduresSearchResults, setAnesthesiaProceduresSearchResults] = useState([]);
  const [anesthesiaProceduresList, setAnesthesiaProceduresList] = useState([]);
  const [isNoneAnesthesiaProcedure, setIsNoneAnesthesiaProcedure] = useState(false);
  // Records -- Lab
  const [labsDocs, setLabsDocs] = useState([]);
  const [isNoneLabsDocs, setIsNoneLabsDocs] = useState(true);
  const [labsFormType, setLabsFormType] = useState('Labs');
  // Records -- EKG
  const [ekgDocs, setEkgDocs] = useState([]);
  const [isNoneEkgDocs, setIsNoneEkgDocs] = useState(true);
  const [ekgFormType, setEkgFormType] = useState('EKG');
  // Records -- Imaging
  const [imagingDocs, setImagingDocs] = useState([]);
  const [isNoneImagingDocs, setIsNoneImagingDocs] = useState(true);
  const [imagingFormType, setImagingFormType] = useState('Imaging');
  // Records -- Consults
  const [consultsDocs, setConsultsDocs] = useState([]);
  const [isNoneConsults, setIsNoneConsults] = useState(true);
  const [consultsFormType, setConsultsFormType] = useState('Consults');
  // Nursing
  const [nursingFormType, setNursingFormType] = useState('Nursing');
  const [isNoneNursing, setIsNoneNursing] = useState(true);
  const [nursingDocs, setNursingDocs] = useState([]);
  // Surgeon
  const [surgeonFormType, setSurgeonFormType] = useState('Surgeon');
  const [isNoneSurgeon, setIsNoneSurgeon] = useState(true);
  const [surgeonDocs, setSurgeonDocs] = useState([]);
  // Orders - PreOp Form Docs
  const [ordersPreOpFormDocs, setOrdersPreOpFormDocs] = useState([]);
  const [isNoneOrdersPreOpFormDocs, setIsNoneOrdersPreOpFormDocs] = useState(true);
  const [ordersPreOpFormType, setOrdersPreOpFormType] = useState('PreOp Form');
  // Orders - IntraOp Form Docs
  const [ordersIntraOpFormDocs, setOrdersIntraOpFormDocs] = useState([]);
  const [isNoneOrdersIntraOpFormDocs, setIsNoneOrdersIntraOpFormDocs] = useState(true);
  const [ordersIntraOpFormType, setOrdersIntraOpFormType] = useState('Intra Op Form');
  // Orders - Post Op Form Docs
  const [ordersPostOpFormDocs, setOrdersPostOpFormDocs] = useState([]);
  const [isNoneOrdersPostOpFormDocs, setIsNoneOrdersPostOpFormDocs] = useState(true);
  const [ordersPostOpFormType, setOrdersPostOpFormType] = useState('PostOp Form');
  // Discharge
  const [dischargeDocs, setDischargeDocs] = useState([]);
  const [isNoneDischarge, setIsNoneDischarge] = useState(true);
  const [dischargeFormType, setDischargeFormType] = useState('Discharge');
  // -- Disccard | Post Case | Post Case
  const [showDisacardDialog1, setShowDiscardDialog1] = useState(false);
  const [showDisacardDialog2, setShowDiscardDialog2] = useState(false);
  const [canPostCase, setCanPostCase] = useState(false);
  const [shouldSaveCase, setShouldSaveCase] = useState(false);

  /* --- Case times  */
  const [showTimesFlag, setShowTimesFlag] = useState(true);
  // Check In Time
  const [checkInHour, setCheckInHour] = useState(24);
  const [checkInMinute, setCheckInMinute] = useState(60);
  const [isCheckInTimePristine, setIsCheckInTimePristine] = useState(true);

  // PreOp Time
  const [preOpHour, setPreOpHour] = useState(24);
  const [preOpMinute, setPreOpMinute] = useState(60);
  const [isPreOpTimePristine, setIsPreOpTimePristine] = useState(true);
  const [showPreOpFlag, setShowPreOpFlag] = useState(true);

  // Anes Start Time
  const [anesStartHour, setAnesStartHour] = useState(24);
  const [anesStartMinute, setAnesStartMinute] = useState(60);
  const [isAnesStartTimePristine, setIsAnesStartTimePristine] = useState(true);
  const [showAnesStartFlag, setShowAnesStartFlag] = useState(true);

  // Room In Time
  const [inHour, setInHour] = useState(24);
  const [inMinute, setInMinute] = useState(60);
  const [isInPristine, setIsInPristine] = useState(true);

  // Surg Start Time
  const [surgStartHour, setSurgStartHour] = useState(24);
  const [surgStartMinute, setSurgStartMinute] = useState(60);
  const [isSurgStartPristine, setIsSurgStartPristine] = useState(true);
  const [showSurgStartFlag, setShowSurgStartFlag] = useState(true);

  // Surg End Time
  const [surgStopHour, setSurgStopHour] = useState(24);
  const [surgStopMinute, setSurgStopMinute] = useState(60);
  const [isSurgStopPristine, setIsSurgStopPristine] = useState(true);
  const [showSurgStopFlag, setShowSurgStopFlag] = useState(true);

  // Close Time
  const [closingHour, setClosingHour] = useState(24);
  const [closingMinute, setClosingMinute] = useState(60);
  const [isClosingPristine, setIsClosingPristine] = useState(true);
  const [showClosingFlag, setShowClosingFlag] = useState(true);
  // Patient Handover
  const [phoHour, setPhoHour] = useState(24);
  const [phoMinute, setPhoMinute] = useState(60);
  const [isPhoPristine, setIsPhoPristine] = useState(true);
  const [showPhoFlag, setShowPhoFlag] = useState(true);

  // Anes Stop Time
  const [anesStopHour, setAnesStopHour] = useState(24);
  const [anesStopMinute, setAnesStopMinute] = useState(60);
  const [isAnesStopTimePristine, setIsAnesStopTimePristine] = useState(true);
  const [showAnesStopFlag, setShowAnesStopFlag] = useState(true);

  // Discharge
  const [dcHour, setDcHour] = useState(24);
  const [dcMinute, setDcMinute] = useState(60);
  const [isDcPristine, setIsDcPristine] = useState(true);
  const [showDischargeFlag, setShowDischargeFlag] = useState(true);

  // --- Anesthesia Data
  // -- ASA Score
  const [asaScore, setAsaScore] = useState('');
  // -- Anesthesia Types
  const [anesthesiaTypes, setAnesthesiaTypes] = useState([]);
  // Anesthesia Modifiers
  const [anesthesiaModifiers, setAnesthesiaModifiers] = useState([]);

  /* ___ REFS ________________________________________________________ */
  const caseTabRef = useRef();
  const procedureSearchMenuRef = useRef();
  const headerMenuRef = useRef();
  const pacuMenuRef = useRef();

  // --- React Router Hooks
  const history = useHistory();
  const { caseId } = useParams();

  /* ___ INITIAL DATA LOAD ___________________________________________ */
  const loadCaseData = () => {
    const formData = new FormData();
    formData.append('caseId', caseId);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/getCaseDataNew`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          const {
            _id,
            schedulerId,
            practices,
            dateOfSurgery,
            isScheduleToFollow,
            scheduleTimeStartHour,
            scheduleTimeStartMinute,
            scheduleTimeEndHour,
            scheduleTimeEndMinute,
            patientType,
            facility,
            selectedFacilityLocation,
            surgeons,
            surgeonPractices,
            surgicalProcedures,
            otherSurgicalProcedures,
            preOpDx,
            otherPreOpDx,
            isSkipPreOpDx,
            postOpDx,
            otherPostOpDx,
            isSameAsPreOpDx,
            anesthesiologists,
            otherAnesthesiologists,
            isNoneAnesthesiologist,
            anesthesiaPractices,
            crnas,
            otherCrnas,
            isNoneCrna,
            crnaPractices,
            lien,
            workmanComp,
            unknownEnd,
            schedulingNotes,
            // faceSheetDocs,
            isNoneFaceSheet,
            consentFormDocs,
            isNoneConsentFormDocs,
            preOpFormDocs,
            isNonePreOpFormDocs,
            intraOpFormDocs,
            isNoneIntraOpFormDocs,
            postOpFormDocs,
            isNonePostOpFormDocs,
            patientId,
            otherAnesthesiaProcedures,
            anesthesiaTypes,
            anesthesiaPositions,
            anesthesiaModifiers,
            isNoneAnesthesiaModifiers,
            piFlag,
            siFlag,
            tiFlag,
            spFlag,
            patientFlag,
            isNonePreOpForm,
            isNoneIntraOpForm,
            isNonePostOpForm,
            isNoneAnesthesiaConsentForm,
            caseFlag,
            status,
            creationDate,
            anesthesiaProcedures
          } = res.caseData;

          // -- Date of Surgery
          const tmpDos1 = dateOfSurgery.split('T');
          const tmpDos2 = tmpDos1[0].split('-');
          const dosDate = +tmpDos2[2];
          const dosMonth = +tmpDos2[1] - 1;
          const dosYear = +tmpDos2[0];
          if (!res.caseData.isDosTbd) {
            setPatientDos(`${padZero(+dosYear)}-${padZero(+dosMonth + 1)}-${padZero(+dosDate)}T00:00:00.000Z`);
          } else {
            setPatientDos('');
          }

          if (res.caseData.patientType && res.caseData.patientType.length > 0) {
            setPatientAdmType(res.caseData.patientType);
          } else {
            setPatientAdmType('Not Set');
          }

          // -- Scheduled Time
          const scheduleStartHour = scheduleTimeStartHour;
          const scheduleStartMin = scheduleTimeStartMinute;
          const scheduleEndHour = scheduleTimeEndHour;
          const scheduleEndMin = scheduleTimeEndMinute;
          // Case data debug output
          console.dir({
            ...res.caseData,
            dosDate,
            dosMonth,
            dosYear
          });

          // Anesthesia procedures
          setAnesthesiaProceduresList(res.caseData.anesthesiaProcedures.concat(res.caseData.otherAnesthesiaProcedures));
          setIsNoneAnesthesiaProcedure(res.caseData.isNoneAnesthesiaProcedure || false);
          // FaceSheet docs
          setFaceSheetDocs(res.caseData.faceSheetDocs);
          setIsNoneFaceSheet(res.caseData.isNoneFaceSheet);
          // Recoards Docs
          setLabsDocs(res.caseData.labsDocs);
          setIsNoneLabsDocs(res.caseData.isNoneLabDocs);
          setEkgDocs(res.caseData.ekgDocs);
          setIsNoneEkgDocs(res.caseData.isNoneEkgDocs);
          setImagingDocs(res.caseData.imagingDocs);
          setIsNoneImagingDocs(res.caseData.isNoneImagingDocs);
          setConsultsDocs(res.caseData.consultsDocs);
          setIsNoneConsults(res.caseData.isNoneConsults);
          // Consents docs
          setConsentFormDocs(res.caseData.consentFormDocs);
          setIsNoneConsentFormDocs(res.caseData.isNoneConsentFormDocs);
          setConsentSurgFormDocs(res.caseData.consentSurgFormDocs);
          setIsNoneConsentSurgFormDocs(res.caseData.isNoneConsentSurgFormDocs);
          // Nursing
          setNursingDocs(res.caseData.nursingDocs);
          setIsNoneNursing(res.caseData.isNoneNursingDocs);
          // Surgeon Docs
          setSurgeonDocs(res.caseData.surgeonDocs);
          setIsNoneSurgeon(res.caseData.isNoneSurgeonDocs);
          // Anestehsia Docs
          setPreOpFormDocs(res.caseData.preOpFormDocs);
          setIsNonePreOpFormDocs(res.caseData.isNonePreOpFormDocs)
          setIntraOpFormDocs(res.caseData.intraOpFormDocs);
          setIsNoneIntraOpFormDocs(res.caseData.isNoneIntraOpFormDocs)
          setPostOpFormDocs(res.caseData.postOpFormDocs);
          setIsNonePostOpFormDocs(res.caseData.isNonePostOpFormDocs);
          // Orders Docs
          setOrdersPreOpFormDocs(res.caseData.ordersPreOpDocs);
          setIsNoneOrdersPreOpFormDocs(res.caseData.isNoneOrdersPreOpDocs);
          setOrdersIntraOpFormDocs(res.caseData.ordersIntraOpDocs);
          setIsNoneOrdersIntraOpFormDocs(res.caseData.isNoneOrdersIntraOpDocs);
          setOrdersPostOpFormDocs(res.caseData.ordresPostOpDocs);
          setIsNoneOrdersPostOpFormDocs(res.caseData.isNoneOrdersPostOpDocs);
          // Dischange Docs
          setDischargeDocs(res.caseData.dischargeDocs);
          setIsNoneDischarge(res.caseData.isNoneDischargeDocs);

          // -- Anesthesia Procedures
          setAnesthesiaProcedureValue();

          // Anesthesia Interop Data
          setAsaScore(res.caseData.asaScore || []);
          setAnesthesiaTypes(res.caseData.anesthesiaTypes || []);
          setAnesthesiaModifiers(res.caseData.anesthesiaModifiers || []);

          // Times
          if (res.caseData.checkInHour !== undefined && res.caseData.checkInMinute !== undefined) {
            setCheckInHour(res.caseData.checkInHour);
            setCheckInMinute(res.caseData.checkInMinute);
            setIsCheckInTimePristine(false);
          } else {
            setCheckInHour(24);
            setCheckInMinute(60);
          }

          if (res.caseData.preOpHour !== undefined && res.caseData.preOpMinute !== undefined) {
            setPreOpHour(res.caseData.preOpHour);
            setPreOpMinute(res.caseData.preOpMinute);
            setIsPreOpTimePristine(false);
          } else {
            setPreOpHour(24);
            setPreOpMinute(60);
          }

          if (res.caseData.anesthesiaStartHour !== undefined && res.caseData.anesthesiaStartMinute !== undefined) {
            setAnesStartHour(res.caseData.anesthesiaStartHour);
            setAnesStartMinute(res.caseData.anesthesiaStartMinute);
            setIsAnesStartTimePristine(false);
          } else {
            setAnesStartHour(24);
            setAnesStartMinute(60);
          }

          if (res.caseData.inHour !== undefined && res.caseData.inMinute !== undefined) {
            setInHour(res.caseData.inHour);
            setInMinute(res.caseData.inMinute);
            setIsInPristine(false);
          } else {
            setInHour(24);
            setInMinute(60);
          }

          if (res.caseData.surgeryStartHour !== undefined && res.caseData.surgeryStartMinute !== undefined) {
            setSurgStartHour(res.caseData.surgeryStartHour);
            setSurgStartMinute(res.caseData.surgeryStartMinute);
            setIsSurgStartPristine(false);
          } else {
            setSurgStartHour(24);
            setSurgStartMinute(60);
          }

          if (res.caseData.surgeryEndHour !== undefined && res.caseData.surgeryEndMinute !== undefined) {
            setSurgStopHour(res.caseData.surgeryEndHour);
            setSurgStopMinute(res.caseData.surgeryEndMinute);
            setIsSurgStopPristine(false);
          } else {
            setSurgStopHour(24);
            setSurgStopMinute(60);
          }

          if (res.caseData.closingHour !== undefined && res.caseData.closingMinute !== undefined) {
            setClosingHour(res.caseData.closingHour);
            setClosingMinute(res.caseData.closingMinute);
            setIsClosingPristine(false);
          } else {
            setClosingHour(24);
            setClosingMinute(60);
          }

          setPatientHandover(res.caseData.pho);
          if (res.caseData.phoHour !== undefined && res.caseData.phoMinute !== undefined) {
            setPhoHour(res.caseData.phoHour);
            setPhoMinute(res.caseData.phoMinute);
            setIsPhoPristine(false);
          } else {
            setPhoHour(24);
            setPhoMinute(60);
          }

          if (res.caseData.anesthesiaEndHour !== undefined && res.caseData.anesthesiaEndMinute !== undefined) {
            setAnesStopHour(res.caseData.anesthesiaEndHour);
            setAnesStopMinute(res.caseData.anesthesiaEndMinute);
            setIsAnesStopTimePristine(false);
          } else {
            setAnesStopHour(24);
            setAnesStopMinute(60);
          }

          if (res.caseData.dcHour !== undefined && res.caseData.dcMinute !== undefined) {
            setDcHour(res.caseData.dcHour);
            setDcMinute(res.caseData.dcMinute);
            setIsDcPristine(false);
          } else {
            setDcHour(24);
            setDcMinute(60);
          }

          // --- Case data
          // console.log('🔥🔥 ___________ 🔥🔥');
          // console.log(res.caseData);
          setCaseData({
            ...res.caseData,
            // Date of Surgery
            surgeonsList: res.caseData.surgeons,
            crnasList: res.caseData.crnas,
            anesthesiologistsList: res.caseData.anesthesiologists,
            dosDate,
            dosMonth,
            dosYear,
            isDosTbd: res.caseData.isDosTbd,
            isDosPristine: res.caseData.isDosTbd,
            showDosAlert: false,
            // Scheduled Time
            scheduleStartHour,
            scheduleStartMin,
            scheduleEndHour,
            scheduleEndMin,
            isScheduleStartTimePristine: false,
            isScheduleEndTimePristine: false,
            isScheduleTimeTF: isScheduleToFollow,
            stAlertFlag: false,
            stInCompleteFlag: false,
            // Admission Type
            admissionType: patientType,
            // Facility Location
            selectedLocation: selectedFacilityLocation,
            // Surgical Procedures
            proceduresList: surgicalProcedures.concat(otherSurgicalProcedures),
            // PreOp Dx
            preOpDxList: preOpDx.concat(otherPreOpDx),
            // postOpDx
            postOpDxList: postOpDx.concat(otherPostOpDx),
            isSameAsPreOpDx
          });
          setPatientData(res.patientData);
          // console.log('🔥🧨 -------------- 🔥🧨');
          // console.log(res.patientData);
          // console.log('🔥🧨 -------------- 🔥🧨');
          setPatientFlag(Object.keys(res.patientData).length > 0 ? FLAG_VALUE.success : FLAG_VALUE.warning);
        }
      })
      .catch(error => console.log('error', error));
  };

  /* ___ WINDOW EVENTS _______________________________________________ */
  const handleWindowResize = () => {
    setIsMobileLayout(window.innerWidth < MOBILE_BREAK_POINT);
  };

  /* ___ EFFECTS _____________________________________________________ */
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();

    const usr = localStorage.getItem('usr');
    if (usr) {
      setUser(JSON.parse(usr));
    }

    if (Object.keys(caseData).length === 0) {
      loadCaseData();
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // --- Payer Flag
  useEffect(() => {
    let piFlag = FLAG_VALUE.warning;
    let siFlag = FLAG_VALUE.warning;
    let tiFlag = FLAG_VALUE.warning;
    let spFlag = FLAG_VALUE.warning;

    // Primary Insurance Flag
    if (patientData.isNoPrimaryInsurance) {
      piFlag = FLAG_VALUE.success;
    } else {
      if (patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0) {
        piFlag = FLAG_VALUE.success;
      } else {
        piFlag = FLAG_VALUE.warning;
      }
    }

    // Secondary Insurance Flag
    if (patientData.isNoSecondaryInsurance) {
      siFlag = FLAG_VALUE.success;
    } else {
      if (patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0) {
        siFlag = FLAG_VALUE.success;
      } else {
        siFlag = FLAG_VALUE.warning;
      }
    }

    // Tertiary Insurance Flag
    if (patientData.isNoTertiaryInsurance) {
      tiFlag = FLAG_VALUE.success;
    } else {
      if (patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0) {
        tiFlag = FLAG_VALUE.success;
      } else {
        tiFlag = FLAG_VALUE.warning;
      }
    }

    // Self Pay Flag
    if (patientData.isNoSelfPay) {
      spFlag = FLAG_VALUE.success;
    } else {
      spFlag = FLAG_VALUE.warning;
    }

    setPayerFlag(findFlag(piFlag, siFlag, tiFlag, spFlag));
  }, [patientData]);

  // Search Anesthesia Procedure Value
  // --- Surgical Procedures Search ---
  const searchSurgicalProcedures = (value) => {
    const url = new URL(`${BASE_URL}/anesProc/searchAnesthesiaProcedures`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowAnesthesiaProceduresSearchMenu(true);
        setAnesthesiaProceduresSearchResults(data.anesthesiaProcedures);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedProceduresSearch = useCallback(debounce(value => searchSurgicalProcedures(value), 800), []);

  useEffect(() => {
    if (anesthesiaProcedureValue && anesthesiaProcedureValue.length === 0) {
      setAnesthesiaProceduresSearchResults([]);
    }
    if (anesthesiaProcedureValue && anesthesiaProcedureValue.trim().length > 0) {
      debouncedProceduresSearch(anesthesiaProcedureValue);
    }
  }, [anesthesiaProcedureValue]);

  // Schedule drawer ** Master Flag **
  useEffect(() => {
    setMasterFlag(findFlag(caseFlag, patientFlag, payerFlag));
  }, [
    caseFlag,
    patientFlag,
    payerFlag
  ]);

  // Case Times Flags ( For Each Step)
  useEffect(() => {
    const checkInTime = `${checkInMinute < 10 ? checkInHour * 10 : checkInHour}${checkInMinute}`;
    const preOpTime = `${preOpMinute < 10 ? preOpHour * 10 : preOpHour}${preOpMinute}`;
    const anesStartTime = `${anesStartMinute < 10 ? anesStartHour * 10 : anesStartHour}${anesStartMinute}`;
    const surgStartTime = `${surgStartMinute < 10 ? surgStartHour * 10 : surgStartHour}${surgStartMinute}`;
    const inTime = `${inMinute < 10 ? inHour * 10 : inHour}${inMinute}`;
    const surgStopTime = `${surgStopMinute < 10 ? surgStopHour * 10 : surgStopHour}${surgStopMinute}`;
    const closingTime = `${closingMinute < 10 ? closingHour * 10 : closingHour}${closingMinute}`;
    const phoTime = `${phoMinute < 10 ? phoHour * 10 : phoHour}${phoMinute}`;
    const anesStopTime = `${anesStopMinute < 10 ? anesStopHour * 10 : anesStopHour}${anesStopMinute}`;
    const dcTime = `${dcMinute < 10 ? dcHour * 10 : dcHour}${dcMinute}`;

    // console.log(`🩻 checkInTime = ${checkInTime} -- ${typeof checkInHour}, ${typeof checkInMinute}`);
    // console.log(`🩻 preOpTime = ${preOpTime} -- ${typeof preOpHour}, ${typeof preOpMinute}`);
    // console.log(`🩻 anesStartTime = ${anesStartTime} -- ${typeof anesStartHour}, ${typeof anesStartMinute}`);
    // console.log(`🩻 surgStartTime = ${surgStartTime} -- ${typeof surgStartHour}, ${typeof surgStartMinute}`);
    // console.log(`typeof surgStartTime = ${typeof surgStartTime}`);
    // console.log(`🩻 inTime = ${inTime} -- ${typeof inHour}, ${typeof inMinute}`);
    // console.log(`🩻 surgStopTime = ${surgStopTime} -- ${typeof surgStopHour}, ${typeof surgStopMinute}`);
    // console.log(`typeof surgStopTime = ${typeof surgStopTime}`);
    // console.log(`🩻 closingTime = ${closingTime} -- ${typeof closingHour}, ${typeof closingMinute}`);
    // console.log(`🩻 phoTime = ${phoTime} -- ${typeof phoHour}, ${typeof phoMinute}`);
    // console.log(`🩻 anesStopTime = ${anesStopTime} -- ${typeof anesStopHour}, ${typeof anesStopMinute}`);
    // console.log(`🩻 dcTime = ${dcTime} -- ${typeof dcHour}, ${typeof dcMinute}`);

    // Must be 1 min after Check in
    // setShowPreOpFlag(+(`${+preOpHour}${+preOpMinute}`) <= +(`${+checkInHour}${+checkInMinute}`));
    setShowPreOpFlag(+preOpTime <= +checkInTime);
    // Must be 1 min after PreOp
    // setShowAnesStartFlag(+(`${+anesStartHour}${+anesStartMinute}`) <= +(`${+preOpHour}${+preOpMinute}`));
    setShowAnesStartFlag(+anesStartTime <= +preOpTime);
    // Must be 1 min after Room Time
    // setShowSurgStartFlag(+(`${+surgStartHour}${+surgStartMinute}`) <= +(`${+inHour}${+inMinute}`));
    setShowSurgStartFlag(+surgStartTime <= +inTime);
    // Must be 1 min after Surg Start
    setShowSurgStopFlag(+surgStopTime <= +surgStartTime);
    // Must be 1 min after Surg Stop
    // setShowClosingFlag(+(`${+closingHour}${+closingMinute}`) <= +(`${+surgStopHour}${+surgStopMinute}`));
    setShowClosingFlag(+closingTime <= +surgStopTime);
    // Must be 1 min after Room Out
    setShowPhoFlag(+phoTime <= +closingTime);
    // Must be 1 min after PACU/ICU
    setShowAnesStopFlag(+anesStopTime <= +phoTime);
    // Must be 1 min after Anes Stop
    setShowDischargeFlag(+dcTime <= +anesStopTime);
  }, [
    checkInHour,
    checkInMinute,
    preOpHour,
    preOpMinute,
    anesStartHour,
    anesStartMinute,
    anesStopHour,
    anesStopMinute,
    inHour,
    inMinute,
    surgStartHour,
    surgStartMinute,
    surgStopHour,
    surgStopMinute,
    closingHour,
    closingMinute,
    phoHour,
    phoMinute,
    dcHour,
    dcMinute
  ]);

  // Case Times Master Flag
  useEffect(() => {
    if (patientAdmType === 'OutPt') {
      setShowTimesFlag(
        isCheckInTimePristine ||
        showPreOpFlag ||
        showAnesStartFlag ||
        isInPristine ||
        showSurgStartFlag ||
        showSurgStopFlag ||
        showClosingFlag ||
        showPhoFlag ||
        showDischargeFlag
      );
    } else {
      setShowTimesFlag(
        isCheckInTimePristine ||
        showPreOpFlag ||
        showAnesStartFlag ||
        isInPristine ||
        showSurgStartFlag ||
        showSurgStopFlag ||
        showClosingFlag ||
        showPhoFlag
      );
    }
  }, [
    patientAdmType,
    isCheckInTimePristine,
    showPreOpFlag,
    showAnesStartFlag,
    isInPristine,
    showSurgStartFlag,
    showSurgStopFlag,
    showClosingFlag,
    showPhoFlag,
    showDischargeFlag
  ]);

  // Schedule Master Flag
  useEffect(() => {
    /* ----- Case Times ----- */
    const timeFlag = showTimesFlag ? FLAG_VALUE.critical : FLAG_VALUE.success;

    /* ----- Face Sheet Tab Flag ----- */
    let faceSheetFlag = FLAG_VALUE.critical;
    if (isNoneFaceSheet) {
      faceSheetFlag = FLAG_VALUE.none;
    } else if (faceSheetDocs.length > 0 || isNoneFaceSheet) {
      faceSheetFlag = FLAG_VALUE.success;
    } else {
      faceSheetFlag = FLAG_VALUE.critical;
    }

    /* ----- Records Tab Flag ----- */
    let recordsFlag = FLAG_VALUE.critical;
    if (isNoneLabsDocs && isNoneEkgDocs && isNoneImagingDocs && isNoneConsults) {
      recordsFlag = FLAG_VALUE.none;
    } else {
      const labsFlag = labsDocs.length > 0 || isNoneLabsDocs;
      const ekgFlag = ekgDocs.length > 0 || isNoneEkgDocs;
      const imagingFlag = imagingDocs.length > 0 || isNoneImagingDocs;
      const consultsFlag = consultsDocs.length > 0 || isNoneConsults;

      if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
        recordsFlag = FLAG_VALUE.success;
      } else {
        recordsFlag = FLAG_VALUE.critical;
      }
    }

    /* ----- Consent Tab Flag ----- */
    let consentFlag = FLAG_VALUE.critical;
    if (isNoneConsentSurgFormDocs && isNoneConsentFormDocs) {
      consentFlag = FLAG_VALUE.none;
    } else {
      const surgFlag = consentSurgFormDocs.length > 0 || isNoneConsentSurgFormDocs;
      const anesFlag = consentFormDocs.length > 0 || isNoneConsentFormDocs;

      if (surgFlag && anesFlag) {
        consentFlag = FLAG_VALUE.success;
      } else {
        consentFlag = FLAG_VALUE.critical;
      }
    }

    /* ----- Anesthesia Tab Flag ----- */
    let anesthesiaFlag = FLAG_VALUE.critical;
    // PreOp Tab Flag
    let preOpFlagValue = FLAG_VALUE.critical;
    if (isNonePreOpFormDocs) {
      preOpFlagValue = FLAG_VALUE.none;
    } else {
      if (preOpFormDocs.length > 0) {
        preOpFlagValue = FLAG_VALUE.success;
      } else {
        preOpFlagValue = FLAG_VALUE.critical;
      }
    }

    // IntraOp Tab Flag
    let intraOpFlagValue = FLAG_VALUE.critical;
    let anesthesiaDataFlagValue = FLAG_VALUE.critical;
    if (asaScore.length > 0 && anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
      anesthesiaDataFlagValue = FLAG_VALUE.success;
    }
    if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
      intraOpFlagValue = anesthesiaDataFlagValue;
    } else {
      if (isNoneIntraOpFormDocs) {
        intraOpFlagValue = FLAG_VALUE.none;
      } else {
        if (intraOpFormDocs.length > 0) {
          intraOpFlagValue = FLAG_VALUE.success;
        } else {
          intraOpFlagValue = FLAG_VALUE.critical;
        }
      }
    }

    // PostOp Tab Flag
    let postOpFlagValue = FLAG_VALUE.critical;
    if (isNonePostOpFormDocs) {
      postOpFlagValue = FLAG_VALUE.none;
    } else {
      if (postOpFormDocs.length > 0) {
        postOpFlagValue = FLAG_VALUE.success;
      } else {
        postOpFlagValue = FLAG_VALUE.critical;
      }
    }

    // Procedures Tab Flag
    let proceduresFlagValue = FLAG_VALUE.critical;
    if (isNoneAnesthesiaProcedure) {
      proceduresFlagValue = FLAG_VALUE.none;
    } else {
      if (anesthesiaProceduresList.length === 0) {
        proceduresFlagValue = FLAG_VALUE.critical;
      } else if (anesthesiaProceduresList.length > 0 && (anesthesiaProceduresList.filter(el => el.npi === 0)).length > 0) {
        proceduresFlagValue = FLAG_VALUE.warning;
      } else if (anesthesiaProceduresList.length > 0 && (anesthesiaProceduresList.filter(el => el.npi === 0)).length === 0) {
        proceduresFlagValue = FLAG_VALUE.success;
      } else {
        proceduresFlagValue = FLAG_VALUE.critical;
      }
    }

    const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
    switch (anesthesiaTabFlag) {
      case FLAG_VALUE.critical:
        anesthesiaFlag = FLAG_VALUE.critical;
        break;
      case FLAG_VALUE.warning:
        anesthesiaFlag = FLAG_VALUE.warning;
        break;
      case FLAG_VALUE.success:
        anesthesiaFlag = FLAG_VALUE.success;
        break;
      case FLAG_VALUE.none:
        anesthesiaFlag = FLAG_VALUE.none;
        break;
      default:
        anesthesiaFlag = FLAG_VALUE.critical;
    }

    /* ----- Nursing Tab Flag ----- */
    let nursingFlag = FLAG_VALUE.critical;
    if (isNoneNursing) {
      nursingFlag = FLAG_VALUE.none;
    } else {
      if (nursingDocs.length > 0 || isNoneNursing) {
        nursingFlag = FLAG_VALUE.success;
      } else {
        nursingFlag = FLAG_VALUE.critical;
      }
    }

    /* ----- Discharge Tab Flag ----- */
    let dcFlag = FLAG_VALUE.critical;
    if (isNoneDischarge) {
      dcFlag = FLAG_VALUE.none;
    } else {
      if (dischargeDocs.length > 0 || isNoneDischarge) {
        dcFlag = FLAG_VALUE.success;
      }
      dcFlag = FLAG_VALUE.critical;
    }

    /* ----- Orders Tab Flag ----- */
    let ordersFlag = FLAG_VALUE.critical;
    if (isNoneOrdersPreOpFormDocs && isNoneOrdersIntraOpFormDocs && isNoneOrdersPostOpFormDocs) {
      ordersFlag = FLAG_VALUE.none;
    }
    const preOPFlag = ordersPreOpFormDocs.length > 0 || isNoneOrdersPreOpFormDocs;
    const intraOpFlag = ordersIntraOpFormDocs.length > 0 || isNoneOrdersIntraOpFormDocs;
    const postOpFlag = ordersPostOpFormDocs.length > 0 || isNoneOrdersPostOpFormDocs;

    if (preOPFlag && intraOpFlag && postOpFlag) {
      ordersFlag = FLAG_VALUE.success;
    } else {
      ordersFlag = FLAG_VALUE.critical;
    }

    // Setting master scheudle flag
    // console.log('---- 🔥 Schedule Master Flag Debug Data 🔥 ----');
    // console.log(`masterFlag = ${masterFlag}`);
    // console.log(`timeFlag = ${timeFlag}`);
    // console.log(`faceSheetFlag = ${faceSheetFlag}`);
    // console.log(`recordsFlag = ${recordsFlag}`);
    // console.log(`consentFlag = ${consentFlag}`);
    // console.log(`anesthesiaFlag = ${anesthesiaFlag}`);
    // console.log(`nursingFlag = ${nursingFlag}`);
    // console.log(`dcFlag = ${dcFlag}`);
    // console.log(`ordersFlag = ${ordersFlag}`);
    // console.log('---------------------------');
    const tmp = findFlag(
      masterFlag,
      timeFlag,
      faceSheetFlag,
      recordsFlag,
      consentFlag,
      anesthesiaFlag,
      nursingFlag,
      dcFlag,
      ordersFlag
    );
    // console.log(`scheduleMasterFlag = ${scheduleMasterFlag}`);
    // console.log('---------------------------\n');
    setScheduleMasterFlag(findFlag(
      masterFlag,
      timeFlag,
      faceSheetFlag,
      recordsFlag,
      consentFlag,
      anesthesiaFlag,
      nursingFlag,
      dcFlag,
      ordersFlag
    ));
  }, [
    masterFlag,
    showTimesFlag,
    isNoneFaceSheet,
    faceSheetDocs,
    labsDocs,
    isNoneLabsDocs,
    ekgDocs,
    isNoneEkgDocs,
    imagingDocs,
    isNoneImagingDocs,
    consultsDocs,
    isNoneConsults,
    consentSurgFormDocs,
    isNoneConsentSurgFormDocs,
    consentFormDocs,
    isNoneConsentFormDocs,
    preOpFormDocs,
    isNonePreOpFormDocs,
    asaScore,
    anesthesiaTypes,
    anesthesiaModifiers,
    isNoneIntraOpFormDocs,
    intraOpFormDocs,
    postOpFormDocs,
    isNonePostOpFormDocs,
    isNoneAnesthesiaProcedure,
    anesthesiaProceduresList,
    nursingDocs,
    isNoneNursing,
    surgeonDocs,
    isNoneSurgeon,
    dischargeDocs,
    isNoneDischarge,
    isNoneOrdersPreOpFormDocs,
    ordersPreOpFormDocs,
    isNoneOrdersIntraOpFormDocs,
    ordersIntraOpFormDocs,
    isNoneOrdersPostOpFormDocs,
    ordersPostOpFormDocs
  ]);

  /* ___ STYLES ______________________________________________________ */
  const styles = StyleSheet.create({
    dashboardContainer: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: blue[900],
      zIndex: '5000',
      overflow: 'hidden'
    },
    header: {
      height: '40px',
      background: '#004080',
      borderBottom: '1px solid #000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 4px'
    },
    headerText: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: 'bold',
      lineHeight: '16px'
    },
    sectionsContainer: {
      height: 'calc(100% - 40px)',
      display: 'flex',
      overflow: 'hidden'
    },
    tabsContainer: {
      position: 'relative',
      display: 'flex',
      flexFlow: 'column',
      // flexGrow: '1'
      width: showScheduleDrawer ? 'calc(100vw - 355px)' : '100vw',
      maxWidth: showScheduleDrawer ? 'calc(100vw - 355px)' : '100vw'
    },
    scheduleFormContainerMobile: {
      top: '40px',
      right: '0',
      height: 'calc(100vh - 40px)'
      // top: '0px',
      // height: '100vh'
      // boxShadow: '-10px 2px 4px rgba(0, 160, 255, .25)'
    },
    scheduleFormContainer: {
      // transition: 'width 0.1s linear',
      width: '355px',
      borderLeft: `${showScheduleDrawer ? '1px' : '0'} solid ${blue[400]}`,
      background: gray[700],
      position: isMobileLayout ? 'absolute' : 'relative',
      top: '0px'
      // position: 'absolute',
      // right: '0px',
      // height: '100vh',
      // overflowY: 'auto'
    },
    scheduleFormContainerClosed: {
      width: '0',
      display: isMobileLayout ? 'none' : ''
    },
    scheduleFormHeader: {
      display: 'flex',
      alignItems: 'center',
      height: '40px',
      backgroundColor: blue[700],
      padding: '0 8px'
    },
    scheduleFormHeaderTitle: {
      flexGrow: '1'
    },
    scheduleFormHeaderCloseIcon: {
      flexGrow: '0',
      ':hover': {
        color: salmon[500]
      }
    },
    patientStickerContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingRight: '4px',
      height: '141px',
      minHeight: '141px',
      background: blue[900],
      flexGrow: '0'
    },
    docTabs: {
      background: 'black',
      padding: '1px 0',
      flexGrow: '0'
    },
    docContainer: {
      height: 'calc(100% - 206px)'
      // flexGrow: '1'
      // display: 'flex',
      // alignItems: 'center',
      // justifyContent: 'center'
    },
    pdfDocContainer: {
      width: '100%',
      // height: '100%'
      height: 'calc(100% - 32px)'
    },
    docFooter: {
      height: '32px',
      width: '100%',
      background: blue[700],
      flexGrow: '0',
      display: 'flex',
      alignItems: 'center',
      // padding: '0 8px',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    scheduleDrawerTabs: {
      maxHeight: 'calc(100% - 56px)',
      overflowY: 'auto',
      overflowX: 'hidden'
    },
    activeComponentContainer: {
      height: 'calc(100% - 71px)',
      overflowY: 'auto'
    },
    menuItem: {
      cursor: 'pointer',
      background: gray[900],
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      fontSize: '14px',
      marginTop: '1px',
      ':hover': {
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    smallMenuItem: {
      height: '40px',
      background: blue[700],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      cursor: 'pointer',
      ':hover': {
        color: blue[800],
        background: blue[300]
      }
    },
    searchPopupMenuContainer: {
      width: `${DRAWER_WIDTH - 21}px`,
      background: blue[900],
      paddingBottom: '1px',
      borderRadius: '5px',
      overflowY: 'auto',
      maxHeight: '480px'
    },
    ellipsisBtn: {
      position: 'relative',
      width: '24px',
      height: '24px',
      background: blue[200],
      color: gray[600],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '2px'
    }
  });

  /* ___ EVENT HANDLERS ______________________________________________ */
  // #region ** Case | Patient | Payers Tabs *** Event Handlers
  const handleOnChangeCaseFlag = (value) => {
    setCaseFlag(value);
    switch (value) {
      case FLAG_VALUE.critical:
        setCaseTabIcon(CRITIAL_ICON);
        break;
      case FLAG_VALUE.warning:
        setCaseTabIcon(BELL_ICON);
        break;
      case FLAG_VALUE.success:
        setCaseTabIcon(SUCCESS_ICON);
        break;
      default:
        setCaseTabIcon(CRITIAL_ICON);
    }
  };

  const handleOnCaseDataChange = () => {
    if (!shouldSaveCase) {
      setShouldSaveCase(true);
    }
  };

  const handleOnSaveSchedule = (data, closeDrawer = false) => {
    const TODAY = new Date();
    const formData = new FormData();
    // Auth Headers
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    /* --- Case Data ------- */
    const {
      dosYear = TODAY.getDate(),
      dosMonth = TODAY.getMonth(),
      dosDate = TODAY.getFullYear(),
      isDosTbd,
      isScheduleStartTimePristine = true,
      isScheduleEndTimePristine = true,
      scheduleStartHour = TODAY.getHours(),
      scheduleStartMin = TODAY.getMinutes(),
      scheduleEndHour = TODAY.getHours(),
      scheduleEndMin = TODAY.getMinutes(),
      isScheduleTimeTF = false,
      admissionType = '',
      facility = {},
      selectedLocation = 'TBD (To Be Determined)',
      surgeonsList = [],
      surgeonPractices = [],
      proceduresList = [],
      preOpDxList = [],
      isSkipPreOpDx = false,
      postOpDxList = [],
      isSameAsPreOpDx = false,
      anesthesiologistsList = [],
      isNoneAnesthesiologist = false,
      anesthesiaPractices = [],
      crnasList = [],
      isNoneCrna = false,
      crnaPractices = [],
      lien,
      workmanComp,
      unknownEnd,
      schedulingNotes
    } = data;

    // Schedule Id
    // formData.append('scheduleId', caseData._id);
    formData.append('caseId', caseId);
    // DOS
    formData.append('dateOfSurgery', `${dosYear}-${padZero(dosMonth + 1)}-${padZero(dosDate)}T00:00:00.000Z`);
    formData.append('isDosTbd', isDosTbd || false);

    // Scheduled Times
    formData.append('scheduleStartHour', !isScheduleStartTimePristine ? scheduleStartHour : null);
    formData.append('scheduleStartMin', !isScheduleStartTimePristine ? scheduleStartMin : null);
    formData.append('scheduleEndHour', !isScheduleEndTimePristine ? scheduleEndHour : null);
    formData.append('scheduleEndMin', !isScheduleEndTimePristine ? scheduleEndMin : null);
    formData.append('isScheduleTimeTF', JSON.stringify(isScheduleTimeTF));

    // Admission Type
    formData.append('admissionType', admissionType);

    // Facility
    formData.append('facility', JSON.stringify(facility));
    formData.append('selectedFacilityLocation', selectedLocation);

    // Surgeon
    formData.append('surgeons', JSON.stringify(surgeonsList.filter(el => el.npi !== 0)));
    const tmpSurgeonPractices = [];
    surgeonPractices.forEach((el) => {
      const tmpProviders = [];
      if (el.addedProviders.length > 0) {
        el.addedProviders.forEach((provider) => {
          tmpProviders.push({
            providerId: provider.providerId || provider._id,
            providerNpi: provider.providerNpi || provider.npi,
            providerFirstName: provider.providerFirstName || provider.firstName,
            providerLastName: provider.providerLastName || provider.lastName,
            providerMiddleName: provider.providerMiddleName || provider.middleName,
            specialty: provider.specialty,
            avatarImageUrl: provider.avatarImageUrl
          });
        });
      }
      tmpSurgeonPractices.push({
        practiceId: el._id,
        practiceName: el.practiceName,
        practiceNpi: el.npi,
        addedProviders: tmpProviders,
        taxonomy: el.taxonomy,
        avatarImageUrl: el.avatarImageUrl
      });
    });
    formData.append('surgeonPractices', JSON.stringify(tmpSurgeonPractices));

    // Surgical Procedures
    formData.append('procedures', JSON.stringify(proceduresList.filter(el => el.npi !== 0)));
    formData.append('otherProcedures', JSON.stringify(proceduresList.filter(el => el.npi === 0)));
    // --- PreOpDx
    formData.append('preOpDx', JSON.stringify(preOpDxList.filter(el => el.npi !== 0)));
    formData.append('otherPreOpDx', JSON.stringify(preOpDxList.filter(el => el.npi === 0)));
    formData.append('isSkipPreOpDx', JSON.stringify(isSkipPreOpDx));
    // --- PostOpDx
    formData.append('postOpDx', JSON.stringify(postOpDxList.filter(el => el.npi !== 0)));
    formData.append('otherPostOpDx', JSON.stringify(postOpDxList.filter(el => el.npi === 0)));
    formData.append('isSameAsPreOpDx', JSON.stringify(isSameAsPreOpDx));

    // Anesthesiologist(s)
    formData.append('anesthesiologists', JSON.stringify(anesthesiologistsList.filter(el => el.npi !== 0)));
    formData.append('otherAnesthesiologists', JSON.stringify(anesthesiologistsList.filter(el => el.npi === 0)));
    formData.append('isNoneAnesthesiologist', JSON.stringify(isNoneAnesthesiologist));
    const tmpAnesthesiaPractices = [];
    anesthesiaPractices.forEach((el) => {
      const tmpProviders = [];
      if (el.addedProviders.length > 0) {
        el.addedProviders.forEach((provider) => {
          tmpProviders.push({
            // providerId: provider.providerId,
            // providerNpi: provider.providerNpi,
            // providerFirstName: provider.providerFirstName,
            // providerLastName: provider.providerLastName,
            // providerMiddleName: provider.providerMiddleName,
            providerId: provider.providerId || provider._id,
            providerNpi: provider.providerNpi || provider.npi,
            providerFirstName: provider.providerFirstName || provider.firstName,
            providerLastName: provider.providerLastName || provider.lastName,
            providerMiddleName: provider.providerMiddleName || provider.middleName,
            specialty: provider.specialty,
            avatarImageUrl: provider.avatarImageUrl
          });
        });
      }
      tmpAnesthesiaPractices.push({
        practiceId: el._id,
        practiceName: el.practiceName,
        practiceNpi: el.npi,
        addedProviders: tmpProviders,
        taxonomy: el.taxonomy,
        avatarImageUrl: el.avatarImageUrl
      });
    });
    formData.append('anesthesiaPractices', JSON.stringify(tmpAnesthesiaPractices));

    // CRNA
    formData.append('crnas', JSON.stringify(crnasList.filter(el => el.npi !== 0)));
    formData.append('otherCrnas', JSON.stringify(crnasList.filter(el => el.npi === 0)));
    formData.append('isNoneCrna', JSON.stringify(isNoneCrna));
    const tmpCrnaPractices = [];
    crnaPractices.forEach((el) => {
      const tmpProviders = [];
      if (el.addedProviders.length > 0) {
        el.addedProviders.forEach((provider) => {
          tmpProviders.push({
            // providerId: provider.providerId,
            // providerNpi: provider.providerNpi,
            // providerFirstName: provider.providerFirstName,
            // providerLastName: provider.providerLastName,
            // providerMiddleName: provider.providerMiddleName,
            providerId: provider.providerId || provider._id,
            providerNpi: provider.providerNpi || provider.npi,
            providerFirstName: provider.providerFirstName || provider.firstName,
            providerLastName: provider.providerLastName || provider.lastName,
            providerMiddleName: provider.providerMiddleName || provider.middleName,
            specialty: provider.specialty,
            avatarImageUrl: provider.avatarImageUrl
          });
        });
      }
      tmpCrnaPractices.push({
        practiceId: el._id,
        practiceName: el.practiceName,
        practiceNpi: el.npi,
        addedProviders: tmpProviders,
        taxonomy: el.taxonomy,
        avatarImageUrl: el.avatarImageUrl
      });
    });
    formData.append('crnaPractices', JSON.stringify(tmpCrnaPractices));

    // Anesthsia Procedures
    formData.append('anesthesiaProcedures', JSON.stringify(anesthesiaProceduresList.filter(el => el.npi !== 0)));
    formData.append('otherAnesthesiaProcedures', JSON.stringify(anesthesiaProceduresList.filter(el => el.npi === 0)));
    formData.append('isNoneAnesthesiaProcedures', JSON.stringify(isNoneAnesthesiaProcedure));

    // Anesthesia Data
    formData.append('asa', asaScore);
    formData.append('anesthesiaTypes', JSON.stringify(anesthesiaTypes));
    formData.append('anesthesiaModifiers', JSON.stringify(anesthesiaModifiers));

    // Lien, Workman Comp, Unknown End and Scheduling Notes
    formData.append('lien', lien);
    formData.append('workmanComp', workmanComp);
    formData.append('unknownEnd', unknownEnd);
    formData.append('schedulingNotes', schedulingNotes);

    // --- CASE DOCS ----
    formData.append('faceSheetDocs', JSON.stringify(faceSheetDocs));
    formData.append('isNoneFaceSheet', isNoneFaceSheet);
    formData.append('consentFormDocs', JSON.stringify(consentFormDocs));
    formData.append('isNoneConsentFormDocs', isNoneConsentFormDocs);
    formData.append('consentSurgFormDocs', JSON.stringify(consentSurgFormDocs));
    formData.append('isNoneConsentSurgFormDocs', isNoneConsentSurgFormDocs);
    formData.append('preOpFormDocs', JSON.stringify(preOpFormDocs));
    formData.append('isNonePreOpFormDocs', isNonePreOpFormDocs);
    formData.append('intraOpFormDocs', JSON.stringify(intraOpFormDocs));
    formData.append('isNoneIntraOpFormDocs', isNoneIntraOpFormDocs);
    formData.append('postOpFormDocs', JSON.stringify(postOpFormDocs));
    formData.append('isNonePostOpFormDocs', isNonePostOpFormDocs);
    // Records tab
    formData.append('labsDocs', JSON.stringify(labsDocs));
    formData.append('isNoneLabDocs', JSON.stringify(isNoneLabsDocs));
    formData.append('ekgDocs', JSON.stringify(ekgDocs));
    formData.append('isNoneEkgDocs', JSON.stringify(isNoneEkgDocs));
    formData.append('imagingDocs', JSON.stringify(imagingDocs));
    formData.append('isNoneImagingDocs', JSON.stringify(isNoneImagingDocs));
    formData.append('consultsDocs', JSON.stringify(consultsDocs));
    formData.append('isNoneConsults', JSON.stringify(isNoneConsults));
    // Nursing Tab
    formData.append('nursingDocs', JSON.stringify(nursingDocs));
    formData.append('isNoneNursingDocs', isNoneNursing);
    // Surgeon Tab
    formData.append('surgeonDocs', JSON.stringify(surgeonDocs));
    formData.append('isNoneSurgeonDocs', JSON.stringify(isNoneSurgeon));
    // Orders Tab
    formData.append('ordersPreOpDocs', JSON.stringify(ordersPreOpFormDocs));
    formData.append('isNoneOrdersPreOpDocs', isNoneOrdersPreOpFormDocs);
    formData.append('ordersIntraOpDocs', JSON.stringify(ordersIntraOpFormDocs));
    formData.append('isNoneOrdersIntraOpDocs', JSON.stringify(isNoneOrdersIntraOpFormDocs));
    formData.append('ordresPostOpDocs', JSON.stringify(ordersPostOpFormDocs));
    formData.append('isNoneOrdersPostOpDocs', isNoneOrdersPostOpFormDocs);
    // Dischange Tab
    formData.append('dischargeDocs', JSON.stringify(dischargeDocs));
    formData.append('isNoneDischargeDocs', JSON.stringify(isNoneDischarge));

    // --- PATIENT DATA ---
    formData.append('patientId', patientData._id);

    // --- Scheduler ID ---
    formData.append('schedulerId', user._id);

    /* ------- Schedule Times & Patient Handover (PACU/ICU) ---------- */
    // Check In Time
    if (!isCheckInTimePristine) {
      formData.append('checkInHour', checkInHour);
      formData.append('checkInMinute', checkInMinute);
    }

    // PreOp Time
    if (!isPreOpTimePristine) {
      formData.append('preOpHour', preOpHour);
      formData.append('preOpMinute', preOpMinute);
    }

    // Anesthesia Start Time
    if (!isAnesStartTimePristine) {
      formData.append('anesStartHour', anesStartHour);
      formData.append('anesStartMinute', anesStartMinute);
    }

    // Room In Time
    if (!isInPristine) {
      formData.append('inHour', inHour);
      formData.append('inMinute', inMinute);
    }

    // Surgery Start Time
    if (!isSurgStartPristine) {
      formData.append('surgStartHour', surgStartHour);
      formData.append('surgStartMinute', surgStartMinute);
    }

    // Syurgery End Time
    if (!isSurgStopPristine) {
      formData.append('surgStopHour', surgStopHour);
      formData.append('surgStopMinute', surgStopMinute);
    }

    // Room Out Time
    if (!isClosingPristine) {
      formData.append('closingHour', closingHour);
      formData.append('closingMinute', closingMinute);
    }

    // Patient Handover Time
    formData.append('pho', patientHandover);
    if (!isPhoPristine) {
      formData.append('phoHour', phoHour);
      formData.append('phoMinute', phoMinute);
    }

    // Anesthesia Stop Time
    if (!isAnesStopTimePristine) {
      formData.append('anesStopHour', anesStopHour);
      formData.append('anesStopMinute', anesStopMinute);
    }

    // Discharge Time
    if (!isDcPristine) {
      formData.append('dcHour', dcHour);
      formData.append('dcMinute', dcMinute);
    } else {
      formData.append('dcHour', 'null');
      formData.append('dcMinute', 'null');
    }

    // --- Practices ----
    const schedulePractices = new Set();
    tmpSurgeonPractices.forEach(practice => {
      schedulePractices.add(practice.practiceId);
    });
    tmpAnesthesiaPractices.forEach(practice => {
      schedulePractices.add(practice.practiceId);
    });
    tmpCrnaPractices.forEach(practice => {
      schedulePractices.add(practice.practiceId);
    });
    formData.append('practices', JSON.stringify(Array.from(schedulePractices)));
    // -- Case Flag ---
    formData.append('caseFlag', scheduleMasterFlag);

    const requestOptions = { method: 'POST', headers: myHeaders, body: formData, redirect: 'follow' };
    fetch(`${BASE_URL}/casesSchedule/updateCaseSchedule`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const data = JSON.parse(result);
        if (closeDrawer) {
          // history.push('/schedule');
          history.push('/admin/cases');
        } else {
          setShowSavedMsg(true);
          setShouldSaveCase(false);
          setTimeout(() => {
            setShowSavedMsg(false);
          }, 3000);
        }
      })
      .catch(err => console.log(err));
  };

  const handleOnUpdateCaseData = (data, saveCase, closeDrawer = false) => {
    if (saveCase) {
      handleOnSaveSchedule(data, closeDrawer);
    }
    setCaseData(data);
  };

  const handleOnChangePatientFlag = (value) => {
    setPatientFlag(value);
  };
  // #endregion

  // #region ** Patient Card ** Event Handlers
  const openCasePatientDrawer = () => {
    setShowScheduleDrawer(!showScheduleDrawer);
  };
  // #endregion

  // #region ** Schedule Drawer ** Event Handlers
  const handleOnUpdatePatientCard = (key, value) => {
    if (key === 'dateOfSurgery') {
      const tmp = value.split('/');
      setPatientDos(`${+tmp[2]}-${padZero(+tmp[0] + 1)}-${padZero(+tmp[1])}T00:00:00.000Z`);
    }
    if (key === 'admType') {
      setPatientAdmType(value);
      setDcHour(24);
      setDcMinute(60);
      setShowDischargeFlag(true);
      setIsDcPristine(true);
    }

    if (key === 'facility') {
      setPatientFacility(value);
    }

    if (key === 'surgeons') {
      setPatientSurgeons(value);
    }

    if (key === 'anesthesia') {
      setPatientAnesthesia(value);
    }

    if (key === 'preOpDx') {
      setPatientPreOpDx(value);
    }

    if (key === 'surgicalProcedures') {
      setPatientSurgicalProcedures(value);
    }
  };

  const closeScheduleCaseDrawer = () => {
    if (caseTabRef.current) {
      caseTabRef.current.updateCaseData(false);
    }
    setShowScheduleDrawer(false);
  };

  const handleOnActivateDrawerTab = (currentTab) => {
    if (currentTab === 'case') {
      setDrawerActiveTab('case');
    } else if (currentTab === 'patient') {
      setDrawerActiveTab('patient');
    } else if (currentTab === 'payers') {
      setDrawerActiveTab('payers');
    }
  };

  const handleOnAddPatient = (data) => {
    setPatientData(data);
  };

  const handleOnDeletePatient = () => {
    setPatientData({});
  };

  const handleOnReloadPatientData = () => {
    const url = new URL(`${BASE_URL}/patients/getPatientProfile`);
    const params = { id: patientData._id };
    url.search = new URLSearchParams(params).toString();
    const requestOptions = {
      method: 'GET',
      // headers: reqHeaders,
      redirect: 'follow'
    };
    fetch(url, requestOptions)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          setPatientData(data.patient);
        }
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  // #region ** Case Docs (new) ** Event Handlers

  // Face Sheet
  const handleOnFaceSheetDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setFaceSheetDocs(docs);
  };

  const handleOnChangeIsNoneFaceSheet = (value) => {
    handleOnCaseDataChange();
    setIsNoneFaceSheet(value);
  };

  const handleOnDeleteFaceSheetPage = (docId) => {
    handleOnCaseDataChange();
    setFaceSheetDocs(prvState => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteFaceSheetDocument = () => {
    handleOnCaseDataChange();
    setFaceSheetDocs([]);
  };

  // Consent Surgery
  const handleOnConsentSurgFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setConsentSurgFormDocs(docs);
  };

  const handleOnChangeIsNoneConsentSurgForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneConsentSurgFormDocs(value);
  };

  const handleOnDeleteConsentSurgFormDocPage = (docId) => {
    handleOnCaseDataChange();
    setConsentSurgFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteConsentSurgFormDocDocument = (docId) => {
    handleOnCaseDataChange();
    setConsentSurgFormDocs();
  };

  // Consent Anesthesia
  const handleOnConsentFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setConsentFormDocs(docs);
  };

  const handleOnChangeIsNoneConsentForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneConsentFormDocs(value);
  };

  const handleOnDeleteConsentFormDocPage = (docId) => {
    handleOnCaseDataChange();
    setConsentFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteConsentFormDocDocument = (docId) => {
    handleOnCaseDataChange();
    setConsentFormDocs([]);
  };

  // Anesthesia PrreOP Sub Tab
  const handleOnPreOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setPreOpFormDocs(docs);
  };

  const handleOnChangeIsNonePreOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNonePreOpFormDocs(value);
  };

  const handleOnDeletePreOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setPreOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeletePreOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setPreOpFormDocs([]);
  };

  // Anesthesia IntraOp Sub Tab
  const handleOnIntraOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setIntraOpFormDocs(docs);
  };

  const handleOnChangeIsNoneIntraOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneIntraOpFormDocs(value);
  };

  const handleOnDeleteIntraOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setIntraOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteIntraOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setIntraOpFormDocs([]);
  };

  // Anesthesia PostOp Sub Tab
  const handleOnPostOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setPostOpFormDocs(docs);
  };

  const handleOnChangeIsNonePostOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNonePostOpFormDocs(value);
  };

  const handleOnDeletePostOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setPostOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeletePostOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setPostOpFormDocs([]);
  };
  // #endregion

  // #region Records Event Handlers
  const handleOnLabsDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setLabsDocs(docs);
  };

  const handleOnChangeIsNoneLabsDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneLabsDocs(value);
  }

  const handleOnDeleteLabsDocsPage = (docId) => {
    handleOnCaseDataChange();
    setLabsDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteLabsDocsDocument = () => {
    handleOnCaseDataChange();
    setLabsDocs([]);
  };

  const handleOnEkgDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setEkgDocs(docs);
  };

  const handleOnChangeIsNoneEkgDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneEkgDocs(value);
  }

  const handleOnDeleteEkgDocsPage = (docId) => {
    handleOnCaseDataChange();
    setEkgDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteEkgDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setEkgDocs([]);
  };

  const handleOnImagingDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setImagingDocs(docs);
  };

  const handleOnChangeIsNoneImagingDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneImagingDocs(value);
  }

  const handleOnDeleteImagingDocsPage = (docId) => {
    handleOnCaseDataChange();
    setImagingDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteImagingDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setImagingDocs();
  };

  const handleOnConsultsDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setConsultsDocs(docs);
  };

  const handleOnChangeIsNoneConsultsDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneConsults(value);
  }

  const handleOnDeleteConsultsDocsPage = (docId) => {
    handleOnCaseDataChange();
    setConsultsDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteConsultsDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setConsultsDocs([]);
  };
  // #endregion

  // #region Nursing Event Handlers
  const handleOnNursingDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setNursingDocs(docs);
  };

  const handleOnChangeIsNoneNursingDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneNursing(value);
  };

  const handleOnDeleteNursingDocsPage = (docId) => {
    handleOnCaseDataChange();
    setNursingDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteNursingDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setNursingDocs([]);
  };
  // #endregion

  // #region Surgeon Event Handlers
  const handleOnSurgeonDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setSurgeonDocs(docs);
  };

  const handleOnChangeIsNoneSurgeonDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneSurgeon(value);
  };

  const handleOnDeleteSurgeonDocsPage = (docId) => {
    handleOnCaseDataChange();
    setSurgeonDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteSurgeonDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setSurgeonDocs();
  };
  // #endregion

  // #region Discharage Event Handelers
  const handleOnDischargeDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setDischargeDocs(docs);
  };

  const handleOnChangeIsNoneDischarge = (value) => {
    handleOnCaseDataChange();
    setIsNoneDischarge(value);
  };

  const handleOnDeleteDischargeDocPage = (docId) => {
    handleOnCaseDataChange();
    setDischargeDocs(prvState => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteDischargeDocDocument = (docId) => {
    handleOnCaseDataChange();
    setDischargeDocs([]);
  };
  // #endregion

  // #region Orders PreOp Tab Event Handlers
  const handleOnOrdersPreOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setOrdersPreOpFormDocs(docs);
  };

  const handleOnChangeIsNoneOrdersPreOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneOrdersPreOpFormDocs(value);
  };

  const handleOnDeleteOrdersPreOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setOrdersPreOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteOrdersPreOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setOrdersPreOpFormDocs([]);
  };
  // #endregion

  // #region Orders IntraOp Event Handlers
  const handleOnOrdersIntraOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setOrdersIntraOpFormDocs(docs);
  };

  const handleOnChangeIsNoneOrdersIntraOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneOrdersIntraOpFormDocs(value);
  };

  const handleOnDeleteOrdersIntraOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setOrdersIntraOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteOrdersIntraOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setOrdersIntraOpFormDocs([]);
  };
  // #endregion

  // #region Order PreOp Event Handlers
  const handleOnOrdersPostOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setOrdersPostOpFormDocs(docs);
  };

  const handleOnChangeIsNoneOrdersPostOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneOrdersPostOpFormDocs(value);
  };

  const handleOnDeleteOrdersPostOpFormDocPage = (docId) => {
    handleOnCaseDataChange();
    setOrdersPostOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteOrdersPostOpFormDocDocument = (docId) => {
    handleOnCaseDataChange();
    setOrdersPostOpFormDocs([]);
  };
  // #endregion

  /* _____ MENU ITEMS ________________________________________________ */
  // #region * MENU ITEMS * -- Anesthesia Procedures
  const procedureSearchItems = [];
  // Append search text
  if (anesthesiaProcedureValue && anesthesiaProcedureValue.length > 0) {
    procedureSearchItems.push(
      <div
        key='88888999SurgProcedure'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          const tmpProc = {
            id: Math.random().toString(36).slice(2),
            text: anesthesiaProcedureValue,
            code: '',
            description: anesthesiaProcedureValue,
            npi: 0
          };
          handleOnCaseDataChange();
          setShowAnesthesiaProceduresSearchMenu(false);
          setAnesthesiaProcedureValue('');
          setAnesthesiaProceduresList((prevState) => {
            return prevState.concat(tmpProc);
          });
        }}
      >
        <div className='ml-2'>
          <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
            {anesthesiaProcedureValue}
          </div>
        </div>
      </div>
    );
  }
  if (anesthesiaProceduresSearchResults.length > 0) {
    // Append search results
    anesthesiaProceduresSearchResults.forEach((el) => {
      procedureSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            handleOnCaseDataChange();
            setShowAnesthesiaProceduresSearchMenu(false);
            setAnesthesiaProcedureValue('');
            setAnesthesiaProceduresList((prevState) => {
              if (prevState.findIndex(item => item.id === el._id) < 0) {
                return prevState.concat({
                  id: el._id,
                  text: `${el.description}`,
                  code: el.code,
                  description: `${el.description}`,
                  npi: el.npi
                });
              } else {
                return prevState;
              }
            });
          }}
        >
          <div className='ml-2'>
            <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
              {getHighlightedText(el.description, anesthesiaProcedureValue)}
            </div>
          </div>
        </div>
      );
    });
  }
  // #endregion

  let activeDrawerComponent = null;
  switch (drawerActiveTab) {
    case 'case':
      activeDrawerComponent = (
        <CaseTab
          ref={caseTabRef}
          isDashboard
          onUpdatePatientCard={handleOnUpdatePatientCard}
          onClose={closeScheduleCaseDrawer}
          onSave={handleOnSaveSchedule}
          onDataChange={handleOnCaseDataChange}
          showFooter={false}
          caseData={caseData}
          onUpdateCaseData={handleOnUpdateCaseData}
          onChangeFlag={handleOnChangeCaseFlag}
        />
      );
      break;
    case 'patient':
      activeDrawerComponent = (
        <PatientTab
          patientData={patientData}
          onAddPatient={handleOnAddPatient}
          onDeletePatient={handleOnDeletePatient}
          onChangeFlag={handleOnChangePatientFlag}
        />
      );
      break;
    case 'payers':
      activeDrawerComponent = (
        <PayerTab
          patientData={patientData}
          onReloadPatientData={handleOnReloadPatientData}
        />
      );
      break;
    default:
      activeDrawerComponent = null;
  }

  let activeDocComponent = null;
  switch (activeTab) {
    case TABS[0].text: // FaceSheet
      activeDocComponent = (
        <CaseDoc
          formType={faceSheetFormType}
          caseDocs={faceSheetDocs}
          isNone={isNoneFaceSheet}
          showFlag
          flagType={FLAG_VALUE.critical}
          onChangeIsNone={handleOnChangeIsNoneFaceSheet}
          onCaseDocUploaded={handleOnFaceSheetDocsUploaded}
          onDeletePage={handleOnDeleteFaceSheetPage}
          onDeleteDocument={handleOnDeleteFaceSheetDocument}
        />
      );
      break;
    case TABS[1].text: // Records
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="records_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Labs */}
            <TabItem
              key='1199'
              tabKey='1199'
              parentKey='11999'
              text='Labs'
              isActive={recordsSubTab === 'labs'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneLabsDocs) {
                  return IS_NONE_ICON;
                }
                if (labsDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: recordsSubTab === 'labs' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('labs');
              }}
            >
              Labs
            </TabItem>
            {/* EKG */}
            <TabItem
              key='12992001'
              tabKey='12992001'
              parentKey='12993000'
              text='EKG'
              isActive={recordsSubTab === 'ekg'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneEkgDocs) {
                  return IS_NONE_ICON;
                }
                if (ekgDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: recordsSubTab === 'ekg' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('ekg');
              }}
            >
              EKG
            </TabItem>
            {/* Imaging */}
            <TabItem
              key='19192002'
              tabKey='19192002'
              parentKey='19194000'
              text='PostOp'
              isActive={recordsSubTab === 'imaging'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneImagingDocs) {
                  return IS_NONE_ICON;
                }
                if (imagingDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: recordsSubTab === 'imaging' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('imaging');
              }}
            >
              Imaging
            </TabItem>
            {/* Consults */}
            <TabItem
              key='2003'
              tabKey='2003'
              parentKey='5000'
              text='Procedures'
              isActive={recordsSubTab === 'consults'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneConsults) {
                  return IS_NONE_ICON;
                }
                if (consultsDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: recordsSubTab === 'consults' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('consults');
              }}
            >
              Consults
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {recordsSubTab === 'labs' &&
              <CaseDoc
                formType={labsFormType}
                caseDocs={labsDocs}
                isNone={isNoneLabsDocs}
                showFlag
                flagType={FLAG_VALUE.critical}
                onChangeIsNone={handleOnChangeIsNoneLabsDocs}
                onCaseDocUploaded={handleOnLabsDocsUploaded}
                onDeletePage={handleOnDeleteLabsDocsPage}
                onDeleteDocument={handleOnDeleteLabsDocsDocument}
              />
            }
            {recordsSubTab === 'ekg' &&
              <CaseDoc
                formType={ekgFormType}
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={ekgDocs}
                isNone={isNoneEkgDocs}
                onChangeIsNone={handleOnChangeIsNoneEkgDocs}
                onCaseDocUploaded={handleOnEkgDocsUploaded}
                onDeletePage={handleOnDeleteEkgDocsPage}
                onDeleteDocument={handleOnDeleteEkgDocsDocument}
              />
            }
            {recordsSubTab === 'imaging' &&
              <CaseDoc
                formType={imagingFormType}
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={imagingDocs}
                isNone={isNoneImagingDocs}
                onChangeIsNone={handleOnChangeIsNoneImagingDocs}
                onCaseDocUploaded={handleOnImagingDocsUploaded}
                onDeletePage={handleOnDeleteImagingDocsPage}
                onDeleteDocument={handleOnDeleteImagingDocsDocument}
              />
            }
            {recordsSubTab === 'consults' &&
              <CaseDoc
                formType={consultsFormType}
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={consultsDocs}
                isNone={isNoneConsults}
                onChangeIsNone={handleOnChangeIsNoneConsultsDocs}
                onCaseDocUploaded={handleOnConsultsDocsUploaded}
                onDeletePage={handleOnDeleteConsultsDocsPage}
                onDeleteDocument={handleOnDeleteConsultsDocsDocument}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[2].text: // Consent Forms
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="consent_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Consents - Surgery sub tab */}
            <TabItem
              key='2334'
              tabKey='2334'
              parentKey='1223'
              text='Surgery'
              isActive={consentActiveTab === 'surgery'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneConsentSurgFormDocs) {
                  return IS_NONE_ICON;
                }
                if (consentSurgFormDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: consentActiveTab === 'surgery' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setConsentActiveTab('surgery');
              }}
            >
              Surgery
            </TabItem>
            {/* Consents - Anesthesia sub tab */}
            <TabItem
              key='2333'
              tabKey='2333'
              parentKey='1222'
              text='Anesthesia'
              isActive={consentActiveTab === 'anesthesia'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneConsentFormDocs) {
                  return IS_NONE_ICON;
                }
                if (consentFormDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: consentActiveTab === 'anesthesia' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setConsentActiveTab('anesthesia');
              }}
            >
              Anesthesia
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {consentActiveTab === 'surgery' &&
              <CaseDoc
                formType={consentSurgFormType}
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={consentSurgFormDocs}
                isNone={isNoneConsentSurgFormDocs}
                onChangeIsNone={handleOnChangeIsNoneConsentSurgForm}
                onCaseDocUploaded={handleOnConsentSurgFormDocsUploaded}
                onDeletePage={handleOnDeleteConsentSurgFormDocPage}
                onDeleteDocument={handleOnDeleteConsentSurgFormDocDocument}
              />
            }
            {consentActiveTab === 'anesthesia' &&
              <CaseDoc
                formType={consentFormType}
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={consentFormDocs}
                isNone={isNoneConsentFormDocs}
                onChangeIsNone={handleOnChangeIsNoneConsentForm}
                onCaseDocUploaded={handleOnConsentFormDocsUploaded}
                onDeletePage={handleOnDeleteConsentFormDocPage}
                onDeleteDocument={handleOnDeleteConsentFormDocDocument}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[3].text: // Nursing
      activeDocComponent = (
        <CaseDoc
          formType={nursingFormType}
          caseDocs={nursingDocs}
          isNone={isNoneNursing}
          showFlag
          flagType={FLAG_VALUE.critical}
          onChangeIsNone={handleOnChangeIsNoneNursingDocs}
          onCaseDocUploaded={handleOnNursingDocsUploaded}
          onDeletePage={handleOnDeleteNursingDocsPage}
          onDeleteDocument={handleOnDeleteNursingDocsDocument}
        />
      );
      break;
    case TABS[4].text: // Surgeon
      activeDocComponent = (
        <CaseDoc
          formType={surgeonFormType}
          caseDocs={surgeonDocs}
          isNone={isNoneSurgeon}
          showFlag
          flagType={FLAG_VALUE.critical}
          onChangeIsNone={handleOnChangeIsNoneSurgeonDocs}
          onCaseDocUploaded={handleOnSurgeonDocsUploaded}
          onDeletePage={handleOnDeleteSurgeonDocsPage}
          onDeleteDocument={handleOnDeleteSurgeonDocsDocument}
        />
      );
      break;
    case TABS[5].text: // Anesthesia Forms
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="anesthesia_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* PreOp Tab */}
            <TabItem
              key='2000'
              tabKey='2000'
              parentKey='2000'
              text='PreOp'
              isActive={anesthesiaSubTab === 'preOp'}
              showTabIcon
              showSvgIcon
              // svgIcon={(preOpFormDocs.length > 0 || isNonePreOpFormDocs) ? SUCCESS_ICON : BELL_ICON}
              svgIcon={(() => {
                if (isNonePreOpFormDocs) {
                  return IS_NONE_ICON;
                }
                if (preOpFormDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: anesthesiaSubTab === 'preOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('preOp');
              }}
            >
              PreOp
            </TabItem>
            {/* IntraOp Tab */}
            <TabItem
              key='2001'
              tabKey='2001'
              parentKey='3000'
              text='IntraOp'
              isActive={anesthesiaSubTab === 'intraOp'}
              showTabIcon
              showSvgIcon
              // svgIcon={(intraOpFormDocs.length > 0 || isNoneIntraOpFormDocs) ? SUCCESS_ICON : BELL_ICON}
              svgIcon={(() => {
                let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                if (asaScore.length > 0 && anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
                  anesthesiaDataFlagValue = FLAG_VALUE.success;
                }
                if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                  return CRITIAL_ICON;
                } else {
                  if (isNoneIntraOpFormDocs) {
                    // return IS_NONE_ICON;
                    return SUCCESS_ICON;
                  }
                  if (intraOpFormDocs.length > 0) {
                    return SUCCESS_ICON;
                  } else {
                    return CRITIAL_ICON;
                  }
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: anesthesiaSubTab === 'intraOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('intraOp');
              }}
            >
              IntraOp
            </TabItem>
            {/* PostOp */}
            <TabItem
              key='2002'
              tabKey='2002'
              parentKey='4000'
              text='PostOp'
              isActive={anesthesiaSubTab === 'postOp'}
              showTabIcon
              showSvgIcon
              // svgIcon={(postOpFormDocs.length > 0 || isNonePostOpFormDocs) ? SUCCESS_ICON : BELL_ICON}
              svgIcon={(() => {
                if (isNonePostOpFormDocs) {
                  return IS_NONE_ICON;
                }
                if (postOpFormDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: anesthesiaSubTab === 'postOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('postOp');
              }}
            >
              PostOp
            </TabItem>
            {/* Procedures */}
            <TabItem
              key='2003'
              tabKey='2003'
              parentKey='5000'
              text='Procedures'
              isActive={anesthesiaSubTab === 'procedures'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneAnesthesiaProcedure) {
                  return IS_NONE_ICON;
                }
                if (anesthesiaProceduresList.length === 0) {
                  return CRITIAL_ICON;
                }
                if (anesthesiaProceduresList.length > 0 && (anesthesiaProceduresList.filter(el => el.npi === 0)).length > 0) {
                  return BELL_ICON;
                } else {
                  return SUCCESS_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: anesthesiaSubTab === 'procedures' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('procedures');
              }}
            >
              Procedures
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {anesthesiaSubTab === 'preOp' &&
              <CaseDoc
                formType={preOpFormType}
                caseDocs={preOpFormDocs}
                isNone={isNonePreOpFormDocs}
                showFlag
                flagType={FLAG_VALUE.critical}
                onChangeIsNone={handleOnChangeIsNonePreOpForm}
                onCaseDocUploaded={handleOnPreOpFormDocsUploaded}
                onDeletePage={handleOnDeletePreOpFormmDocPage}
                onDeleteDocument={handleOnDeletePreOpFormmDocDocument}
              />
            }
            {anesthesiaSubTab === 'intraOp' &&
              <IntraOp
                showFlag
                asaScore={asaScore}
                onChangeAsaScore={(asa) => {
                  setAsaScore(asa);
                }}
                anesthesiaTypes={anesthesiaTypes}
                onChangeAnesthesiaTypes={(data) => {
                  setAnesthesiaTypes(data);
                }}
                anesthesiaModifiers={anesthesiaModifiers}
                onChangeAnesthesiaModifiers={(data) => {
                  setAnesthesiaModifiers(data);
                }}
                formType={intraOpFormType}
                flagType={FLAG_VALUE.critical}
                caseDocs={intraOpFormDocs}
                isNone={isNoneIntraOpFormDocs}
                onChangeIsNone={handleOnChangeIsNoneIntraOpForm}
                onCaseDocUploaded={handleOnIntraOpFormDocsUploaded}
                onDeletePage={handleOnDeleteIntraOpFormmDocPage}
                onDeleteDocument={handleOnDeleteIntraOpFormmDocDocument}
              />
            }
            {anesthesiaSubTab === 'postOp' &&
              <CaseDoc
                formType={postOpFormType}
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={postOpFormDocs}
                isNone={isNonePostOpFormDocs}
                onChangeIsNone={handleOnChangeIsNonePostOpForm}
                onCaseDocUploaded={handleOnPostOpFormDocsUploaded}
                onDeletePage={handleOnDeletePostOpFormmDocPage}
                onDeleteDocument={handleOnDeletePostOpFormmDocDocument}
              />
            }
            {anesthesiaSubTab === 'procedures' &&
              <div
                className='m-2'
                style={{ width: `${DRAWER_WIDTH}px` }}
              >
                <div
                  className='mt-4 mb-2 fx fx__v-center'
                  style={{
                    width: '355px',
                    justifyContent: 'space-between'
                  }}
                >
                  <CaseField
                    heading='Anesthesia Procedures'
                    // showAlert={false}
                    showNoneFlag={isNoneAnesthesiaProcedure}
                    showAlert={(() => {
                      if (!isNoneAnesthesiaProcedure) {
                        if (anesthesiaProceduresList.length === 0) {
                          return true;
                        }
                        return false;
                      }
                    })()}
                    showIncomplete={(() => {
                      if (anesthesiaProceduresList.length > 0 && (anesthesiaProceduresList.filter(el => el.npi === 0)).length > 0) {
                        return true;
                      } else {
                        return false;
                      }
                    })()}
                    showPlusIcon={false}
                    className='ml-1'
                  />
                  <Checkbox
                    text='None'
                    isChecked={isNoneAnesthesiaProcedure}
                    onChange={() => {
                      handleOnCaseDataChange();
                      setAnesthesiaProceduresList([]);
                      setIsNoneAnesthesiaProcedure(!isNoneAnesthesiaProcedure);
                    }}
                  />
                </div>
                {/* Anesthesia procedure value textbox & search menu */}
                <div
                  ref={procedureSearchMenuRef}
                  className='mt-0'
                  style={{
                    // border: `1px solid ${blue[700]}`,
                    borderRadius: '4px',
                    position: 'relative'
                  }}
                >
                  <TextInput
                    value={anesthesiaProcedureValue}
                    isDisabled={isNoneAnesthesiaProcedure}
                    placeholder="Search Anesthesia Procedure(s)"
                    disabled={isNoneAnesthesiaProcedure}
                    style={{
                      width: `${DRAWER_WIDTH}px`,
                      fontSize: '14px',
                      padding: '0 4px',
                      color: blue[200],
                      borderRadius: '4px'
                    }}
                    onChange={(e) => {
                      const { value } = e.target;
                      setAnesthesiaProcedureValue(value);
                    }}
                  />
                  {showAnesthesiaProceduresSearchMenu &&
                    <PopupMenu
                      top={35}
                      left={0}
                      zIndex='1550'
                      position="absolute"
                      visible={showAnesthesiaProceduresSearchMenu}
                      parent={procedureSearchMenuRef}
                      onOutsideClick={() => { setShowAnesthesiaProceduresSearchMenu(false); }}
                    >
                      <div className={css(styles.searchPopupMenuContainer)}>
                        {procedureSearchItems.length > 0 &&
                          <div className={`pl-2 fx fx__v-center ${css(styles.suggResults)}`}>
                            Suggested Results
                          </div>
                        }
                        {procedureSearchItems}
                      </div>
                    </PopupMenu>
                  }
                </div>
                {/* Surgical Procedures Cards */}
                {anesthesiaProceduresList.map(dx => {
                  return (
                    <CaseUserCard
                      className='mt-1'
                      avatarImageUrl={null}
                      showCloseIcon
                      noAvatar
                      key={dx.id || dx._id}
                      dataId={dx.id || dx._id}
                      onClose={(id) => {
                        handleOnCaseDataChange();
                        setAnesthesiaProceduresList((prevState) => {
                          return prevState.filter((el) => {
                            if (el.id) {
                              return el.id !== id;
                            }
                            return el._id !== id;
                          });
                        });
                      }}
                    >
                      <div className='ml-2'>
                        {dx.npi === 0 &&
                          <i
                            className='font-16 mr-2 fa fa-bell'
                            style={{ color: salmon[500] }}
                          />
                        }
                        <span>
                          {dx.description}
                        </span>
                      </div>
                    </CaseUserCard>
                  );
                })}
              </div>
            }
          </div>
        </>
      );
      break;
    case TABS[6].text: // Orders
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="orders_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Orders - PreOp Tab */}
            <TabItem
              key='2000334'
              tabKey='2000334'
              parentKey='2000334'
              text='PreOp'
              isActive={ordersSubTab === 'preOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneOrdersPreOpFormDocs) {
                  return IS_NONE_ICON;
                }
                if (ordersPreOpFormDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: ordersSubTab === 'preOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('preOp');
              }}
            >
              PreOp
            </TabItem>
            {/* IntraOp Tab */}
            <TabItem
              key='2001334'
              tabKey='2001334'
              parentKey='3000334'
              text='IntraOp'
              isActive={ordersSubTab === 'intraOp'}
              showTabIcon
              showSvgIcon
              // svgIcon={(intraOpFormDocs.length > 0 || isNoneIntraOpFormDocs) ? SUCCESS_ICON : BELL_ICON}
              svgIcon={(() => {
                if (isNoneOrdersIntraOpFormDocs) {
                  return IS_NONE_ICON;
                }
                if (ordersIntraOpFormDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: ordersSubTab === 'intraOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('intraOp');
              }}
            >
              IntraOp
            </TabItem>
            {/* PostOp */}
            <TabItem
              key='2002'
              tabKey='2002'
              parentKey='4000'
              text='PostOp'
              isActive={ordersSubTab === 'postOp'}
              showTabIcon
              showSvgIcon
              // svgIcon={(postOpFormDocs.length > 0 || isNonePostOpFormDocs) ? SUCCESS_ICON : BELL_ICON}
              svgIcon={(() => {
                if (isNoneOrdersPostOpFormDocs) {
                  return IS_NONE_ICON;
                }
                if (ordersPostOpFormDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: ordersSubTab === 'postOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('postOp');
              }}
            >
              PostOp
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {ordersSubTab === 'preOp' &&
              <CaseDoc
                formType={ordersPreOpFormType}
                caseDocs={ordersPreOpFormDocs}
                isNone={isNoneOrdersPreOpFormDocs}
                showFlag
                flagType={FLAG_VALUE.critical}
                onChangeIsNone={handleOnChangeIsNoneOrdersPreOpForm}
                onCaseDocUploaded={handleOnOrdersPreOpFormDocsUploaded}
                onDeletePage={handleOnDeleteOrdersPreOpFormmDocPage}
                onDeleteDocument={handleOnDeleteOrdersPreOpFormmDocDocument}
              />
            }
            {ordersSubTab === 'intraOp' &&
              <CaseDoc
                formType={ordersIntraOpFormType}
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={ordersIntraOpFormDocs}
                isNone={isNoneOrdersIntraOpFormDocs}
                onChangeIsNone={handleOnChangeIsNoneOrdersIntraOpForm}
                onCaseDocUploaded={handleOnOrdersIntraOpFormDocsUploaded}
                onDeletePage={handleOnDeleteOrdersIntraOpFormmDocPage}
                onDeleteDocument={handleOnDeleteOrdersIntraOpFormmDocDocument}
              />
            }
            {ordersSubTab === 'postOp' &&
              <CaseDoc
                formType={ordersPostOpFormType}
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={ordersPostOpFormDocs}
                isNone={isNoneOrdersPostOpFormDocs}
                onChangeIsNone={handleOnChangeIsNoneOrdersPostOpForm}
                onCaseDocUploaded={handleOnOrdersPostOpFormDocsUploaded}
                onDeletePage={handleOnDeleteOrdersPostOpFormDocPage}
                onDeleteDocument={handleOnDeleteOrdersPostOpFormDocDocument}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[7].text: // Discharge
      activeDocComponent = (
        <CaseDoc
          formType={dischargeFormType}
          caseDocs={dischargeDocs}
          isNone={isNoneDischarge}
          showFlag
          flagType={FLAG_VALUE.critical}
          onChangeIsNone={handleOnChangeIsNoneDischarge}
          onCaseDocUploaded={handleOnDischargeDocsUploaded}
          onDeletePage={handleOnDeleteDischargeDocPage}
          onDeleteDocument={handleOnDeleteDischargeDocDocument}
        />
      );
      break;
    default:
      activeDocComponent = null;
  }

  return (
    <CaseScheduleContext.Provider value={{ readOnlyMode: false }}>
      <div className={css(styles.dashboardContainer)}>
        {/* Saved Msg -- Toast */}
        {showSavedMsg &&
          <Toast
            id={'c1'}
            top="41"
            bottom={null}
            headerText='Information'
            headerIcon='fa fa-info-circle'
            bodyText='Schedule Data Saved!'
            onDismiss={() => {
              setShowSavedMsg(false);
            }}
            showFooter={false}
          />
        }
        {/* Discad Dialog 1 */}
        {showDisacardDialog1 &&
          <DialogBox
            showCloseBtn={false}
            initWidth={DIALOG_WIDTH}
            top={window.innerWidth <= 576 ? 48 : null}
            left={window.innerWidth <= 576 ? 8 : null}
            initHeight={265}
            isOpen={showDisacardDialog1}
            onClose={() => {
              setShowDiscardDialog1(false);
            }}
            maskBackground="#004080"
            maskOpacity={0.5}
            closeOnEscKey
            closeOnMaskClick={false}
            header={{
              overrideTheme: true,
              text: 'Warning! You Have Unsaved Changes!',
              backColor: '#082840',
              foreColor: 'yellow',
              closeColor: '#FF6060',
              fontStyle: 'bold',
              fontSize: '14px',
              icon: 'fa fa-exclamation-triangle'
            }}
          >
            <div className='p-2'>
              <div className='p-2 text-center'>
                What do you want to do?
              </div>
              {/* Save Button */}
              <Button
                className='mt-2'
                btnType='primary'
                style={{
                  height: '47px',
                  width: '334px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}
                onClick={() => {
                  if (caseTabRef.current) {
                    caseTabRef.current.updateCaseData();
                  }
                }}
              >
                Save
              </Button>
              {/* Do tnot save Button */}
              <Button
                className='mt-4'
                btnType='danger'
                style={{
                  height: '32px',
                  width: '334px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => {
                  setShowDiscardDialog1(false);
                  setShowDiscardDialog2(true);
                }}
              >
                Do Not Save
              </Button>
              {/* Cancel Button */}
              <Button
                className='mt-4'
                btnType='primary'
                isOutlineBtn
                style={{
                  border: `1px solid ${gray[400]}`,
                  color: gray[300],
                  height: '32px',
                  width: '334px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowDiscardDialog1(false)}
              >
                Cancel
              </Button>
            </div>
          </DialogBox>
        }

        {/* Discad Dialog 2 */}
        {showDisacardDialog2 &&
          <DialogBox
            showCloseBtn={false}
            initWidth={DIALOG_WIDTH}
            top={window.innerWidth <= 576 ? 48 : null}
            left={window.innerWidth <= 576 ? 8 : null}
            initHeight={265}
            isOpen={showDisacardDialog2}
            onClose={() => {
              setShowDiscardDialog2(false);
            }}
            maskBackground="#004080"
            maskOpacity={0.5}
            closeOnEscKey
            closeOnMaskClick={false}
            header={{
              overrideTheme: true,
              text: 'Really?',
              backColor: '#082840',
              foreColor: 'yellow',
              closeColor: '#FF6060',
              fontStyle: 'bold',
              fontSize: '14px',
              icon: 'fa fa-exclamation-triangle'
            }}
          >
            <div className='p-2'>
              <div className='p-2 text-center'>
                Are you 100% sure?
              </div>
              {/* Save Button */}
              <Button
                className='mt-2'
                btnType='primary'
                style={{
                  height: '47px',
                  width: '334px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}
                onClick={() => {
                  if (caseTabRef.current) {
                    caseTabRef.current.updateCaseData();
                  }
                }}
              >
                Save
              </Button>
              {/* Do not save Button */}
              <Button
                className='mt-4'
                btnType='danger'
                style={{
                  height: '32px',
                  width: '334px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => {
                  setShowDiscardDialog2(false);
                  // history.push('/schedule');
                  history.push('/admin/cases');
                }}
              >
                Do Not Save... I am 100% sure
              </Button>
              {/* Cancel Button */}
              <Button
                className='mt-4'
                btnType='primary'
                isOutlineBtn
                style={{
                  border: `1px solid ${gray[400]}`,
                  color: gray[300],
                  height: '32px',
                  width: '334px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowDiscardDialog2(false)}
              >
                Cancel
              </Button>
            </div>
          </DialogBox>
        }

        {/* --  Main Header ------------------------------------------- */}
        <div className={css(styles.header)}>
          {/* Left side */}
          <div className='fx fx__hv-center'>
            <div className={`pl-1 ${css(styles.headerText)}`}>
              {caseData.facility && caseData.facility.icon &&
                <img
                  className='mr-2'
                  src={caseData.facility.icon}
                  style={{
                    maxHeight: '32px'
                  }}
                />
              }
              {caseData.facility && caseData.facility.facilityName
                ? caseData.facility.facilityName
                : 'Faciity Name N/A'
              }
            </div>
          </div>
          {/* Right side - (Collapse Icon) */}
          <div className='fx fx__hv-center'>
            {/* Schedule Master Flag */}
            {scheduleMasterFlag === FLAG_VALUE.success && SUCCESS_ICON}
            {scheduleMasterFlag === FLAG_VALUE.warning && BELL_ICON}
            {scheduleMasterFlag === FLAG_VALUE.critical && CRITIAL_ICON}
            {/* Header Popup Menu */}
            <div
              className={`ml-3 ${css(styles.ellipsisBtn)}`}
              ref={headerMenuRef}
            >
              <i
                className='fa fa-ellipsis-h'
                onClick={() => {
                  setShowHeaderMenu(true);
                }}
              />
              {showHeaderMenu &&
                <PopupMenu
                  top={32}
                  left={0}
                  zIndex='1550'
                  position="absolute"
                  visible={showHeaderMenu}
                  parent={headerMenuRef}
                  onOutsideClick={() => { setShowHeaderMenu(false); }}
                >
                  <div
                    className={css(styles.searchPopupMenuContainer)}
                    style={{
                      width: '140px'
                    }}
                  >
                    {/* Save Button */}
                    <div
                      className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                      style={{ height: '32px' }}
                      onClick={() => {
                        setShowHeaderMenu(false);
                        if (caseTabRef.current) {
                          caseTabRef.current.updateCaseData(true, false);
                        } else {
                          console.log('❌ caseTabRef is null!');
                        }
                      }}
                    >
                      Save
                    </div>
                    {/* Save and Close */}
                    <div
                      className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                      style={{ height: '32px' }}
                      onClick={() => {
                        setShowHeaderMenu(false);
                        if (caseTabRef.current) {
                          // caseTabRef.current.updateCaseData(true, false);
                          caseTabRef.current.updateCaseData();
                          console.log('✅ Case data updated successfully!');
                        } else {
                          console.log('❌ caseTabRef is null!');
                        }
                        // history.push('/schedule');
                      }}
                    >
                      Save &amp; Close
                    </div>
                  </div>
                </PopupMenu>
              }
            </div>
            {/* Collapse Icon - (was Disabled 07/04/23) */}
            <div
              className='ml-2 mr-2' style={{ height: '24px' }}
              onClick={() => {
                if (shouldSaveCase) {
                  setShowDiscardDialog1(true);
                } else {
                  // history.push('/schedule');
                  history.push('/admin/cases');
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
              >
                <path fill="#96C8FF" fillRule="evenodd" d="M1.926 0h20.145C23.136 0 23.997.861 24 1.926v20.148A1.925 1.925 0 0 1 22.074 24H1.926A1.925 1.925 0 0 1 0 22.074V1.926C0 .861.861 0 1.926 0Zm11.549 11.14h5.738a.786.786 0 1 0 0-1.57H15.44l4.149-4.144a.784.784 0 0 0-.556-1.34.783.783 0 0 0-.556.23l-4.214 4.209v-3.74a.786.786 0 0 0-1.573 0v5.572a.783.783 0 0 0 .785.784ZM4.788 14.43h3.775l-4.15 4.144a.786.786 0 0 0 1.112 1.11l4.216-4.208v3.74a.786.786 0 0 0 1.574 0v-5.572a.786.786 0 0 0-.787-.786H4.789a.786.786 0 0 0-.001 1.572Z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
        {/* --  Main Dashboard Container ------------------------------ */}
        <div className={css(styles.sectionsContainer)}>
          {/* --- Case Docs Tabs -------- */}
          <div className={css(styles.tabsContainer)}>
            {/* Patient Sticker Container */}
            <div className={css(styles.patientStickerContainer)}>
              <PatientCard
                onClickCase={openCasePatientDrawer}
                flagType={findFlag(caseFlag, patientFlag, payerFlag)}
                patientData={{
                  dos: patientDos,
                  patientType: patientAdmType,
                  facilityName: patientFacility,
                  surgeons: patientSurgeons,
                  anesthesia: patientAnesthesia,
                  admDx: patientPreOpDx,
                  surgicalProcedures: patientSurgicalProcedures,
                  firstName: patientData && patientData.firstName ? patientData.firstName : '',
                  middleName: patientData && patientData.middleName ? patientData.middleName : '',
                  lastName: patientData && patientData.lastName ? patientData.lastName : '',
                  gender: patientData && patientData.gender ? patientData.gender : '',
                  dob: patientData && patientData.dob ? patientData.dob : ''
                }}
              />
            </div>

            {/* Document Tabs */}
            <div className={css(styles.docTabs)}>
              <TabsContainer
                dropDownId="caseSchedule_main_tabs"
                showPlusIcon={false}
                isDrawer={false}
                shiftMethod='minWidth'
                minWidth={610}
                containerStyles={{ width: '100%' }}
              >
                {TABS.map((tab) => (
                  <TabItem
                    key={tab.id}
                    tabKey={tab.id}
                    parentKey={tab.pKey}
                    text={tab.text}
                    isActive={activeTab === tab.text}
                    showTabIcon
                    showSvgIcon
                    svgIcon={(() => {
                      switch (tab.text) {
                        case 'FaceSheet':
                          if (isNoneFaceSheet) {
                            return IS_NONE_ICON;
                          }
                          if (faceSheetDocs.length > 0 || isNoneFaceSheet) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        case 'Records': {
                          if (isNoneLabsDocs && isNoneEkgDocs && isNoneImagingDocs && isNoneConsults) {
                            return IS_NONE_ICON;
                          }

                          const labsFlag = labsDocs.length > 0 || isNoneLabsDocs;
                          const ekgFlag = ekgDocs.length > 0 || isNoneEkgDocs;
                          const imagingFlag = imagingDocs.length > 0 || isNoneImagingDocs;
                          const consultsFlag = consultsDocs.length > 0 || isNoneConsults;

                          if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        }
                        case 'Consents': {
                          if (isNoneConsentSurgFormDocs && isNoneConsentFormDocs) {
                            return IS_NONE_ICON;
                          }

                          const surgFlag = consentSurgFormDocs.length > 0 || isNoneConsentSurgFormDocs;
                          const anesFlag = consentFormDocs.length > 0 || isNoneConsentFormDocs;

                          if (surgFlag && anesFlag) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        }
                        case 'Anesthesia': {
                          // PreOp Tab Flag
                          let preOpFlagValue = FLAG_VALUE.critical;
                          if (isNonePreOpFormDocs) {
                            preOpFlagValue = FLAG_VALUE.none;
                          } else {
                            if (preOpFormDocs.length > 0) {
                              preOpFlagValue = FLAG_VALUE.success;
                            } else {
                              preOpFlagValue = FLAG_VALUE.critical;
                            }
                          }

                          // IntraOp Tab Flag
                          let intraOpFlagValue = FLAG_VALUE.critical;
                          let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                          if (asaScore.length > 0 && anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
                            anesthesiaDataFlagValue = FLAG_VALUE.success;
                          }
                          if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                            intraOpFlagValue = anesthesiaDataFlagValue;
                          } else {
                            if (isNoneIntraOpFormDocs) {
                              intraOpFlagValue = FLAG_VALUE.none;
                            } else {
                              if (intraOpFormDocs.length > 0) {
                                intraOpFlagValue = FLAG_VALUE.success;
                              } else {
                                intraOpFlagValue = FLAG_VALUE.critical;
                              }
                            }
                          }

                          // PostOp Tab Flag
                          let postOpFlagValue = FLAG_VALUE.critical;
                          if (isNonePostOpFormDocs) {
                            postOpFlagValue = FLAG_VALUE.none;
                          } else {
                            if (postOpFormDocs.length > 0) {
                              postOpFlagValue = FLAG_VALUE.success;
                            } else {
                              postOpFlagValue = FLAG_VALUE.critical;
                            }
                          }

                          // Procedures Tab Flag
                          let proceduresFlagValue = FLAG_VALUE.critical;
                          if (isNoneAnesthesiaProcedure) {
                            proceduresFlagValue = FLAG_VALUE.none;
                          } else {
                            if (anesthesiaProceduresList.length === 0) {
                              proceduresFlagValue = FLAG_VALUE.critical;
                            } else if (anesthesiaProceduresList.length > 0 && (anesthesiaProceduresList.filter(el => el.npi === 0)).length > 0) {
                              proceduresFlagValue = FLAG_VALUE.warning;
                            } else if (anesthesiaProceduresList.length > 0 && (anesthesiaProceduresList.filter(el => el.npi === 0)).length === 0) {
                              proceduresFlagValue = FLAG_VALUE.success;
                            } else {
                              proceduresFlagValue = FLAG_VALUE.critical;
                            }
                          }

                          const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
                          switch (anesthesiaTabFlag) {
                            case FLAG_VALUE.critical:
                              return CRITIAL_ICON;
                            case FLAG_VALUE.warning:
                              return BELL_ICON;
                            case FLAG_VALUE.success:
                              return SUCCESS_ICON;
                            case FLAG_VALUE.none:
                              return IS_NONE_ICON;
                            default:
                              return CRITIAL_ICON;
                          }
                        }
                        case 'Nursing':
                          if (isNoneNursing) {
                            return IS_NONE_ICON;
                          }
                          if (nursingDocs.length > 0 || isNoneNursing) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        case 'Surgeon':
                          if (isNoneSurgeon) {
                            return IS_NONE_ICON;
                          }
                          if (surgeonDocs.length > 0 || isNoneSurgeon) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        case 'Discharge':
                          if (isNoneDischarge) {
                            return IS_NONE_ICON;
                          }
                          if (dischargeDocs.length > 0 || isNoneDischarge) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        case 'Orders': {
                          if (isNoneOrdersPreOpFormDocs && isNoneOrdersIntraOpFormDocs && isNoneOrdersPostOpFormDocs) {
                            return IS_NONE_ICON;
                          }
                          const preOPFlag = ordersPreOpFormDocs.length > 0 || isNoneOrdersPreOpFormDocs;
                          const intraOpFlag = ordersIntraOpFormDocs.length > 0 || isNoneOrdersIntraOpFormDocs;
                          const postOpFlag = ordersPostOpFormDocs.length > 0 || isNoneOrdersPostOpFormDocs;

                          if (preOPFlag && intraOpFlag && postOpFlag) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        }
                        default:
                          return CRITIAL_ICON;
                      }
                    })()}
                    style={{
                      ...tab.styles,
                      background: activeTab === tab.text ? '#184878' : '#083050'
                    }}
                    textStyles={TABS_TEXT_STYLES}
                    onActivate={() => {
                      setActiveTab(tab.text);
                    }}
                  >
                    {!tab.isIconText ? tab.text : tab.textIcon}
                  </TabItem>
                ))}
              </TabsContainer>
            </div>

            {/* Active Component - Doc Container */}
            <div className={css(styles.docContainer)}>
              {activeDocComponent}
            </div>

            {/* Doc Footer */}
            <div className={css(styles.docFooter)}>
              {/* Footer Flag */}
              <div
                style={{
                  height: '32px',
                  width: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: `1px solid ${blue[900]}`,
                  paddingBottom: '2px'
                }}
              >
                {showTimesFlag ? <CriticalWarningIcon height={14} /> : <SuccessIcon height={14} />}
              </div>
              {/* Check In */}
              <div
                className='ml-2 fx'
                style={{ alignItems: 'baseline' }}
              >
                <div style={{ fontSize: '12px', fontWeight: '700' }}>
                  CheckIn
                </div>
                <TimePicker
                  className='pl-1 pr-2'
                  hour={checkInHour}
                  minute={checkInMinute}
                  color={isCheckInTimePristine ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isCheckInTimePristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsCheckInTimePristine(false);
                    setCheckInHour(hour);
                    setCheckInMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* PreOp */}
              <div
                className='fx'
                style={{ alignItems: 'baseline' }}
              >
                <div style={{ fontSize: '12px', fontWeight: '700' }}>
                  PreOp
                </div>
                <TimePicker
                  className='ml-2 pl-1 pr-2'
                  hour={preOpHour}
                  minute={preOpMinute}
                  // color={isPreOpTimePristine ? yellow[500] : blue[500]}
                  color={(showPreOpFlag || isPreOpTimePristine) ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isPreOpTimePristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsPreOpTimePristine(false);
                    setPreOpHour(hour);
                    setPreOpMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* AnesStart */}
              <div className='ml-2 fx fx__v-center'>
                <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" className='mr-1'>
                    <path fill="#96C8FF" fillRule="evenodd" d="M11.25.804 3 6l8.25 5.196V.804ZM0 12h2V0H0v12Z" clipRule="evenodd" />
                  </svg>
                  Anes
                </div>
                <TimePicker
                  className='pl-1 pr-2'
                  hour={anesStartHour}
                  minute={anesStartMinute}
                  // color={isAnesStartTimePristine ? yellow[500] : blue[500]}
                  color={(isAnesStartTimePristine || showAnesStartFlag) ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isAnesStartTimePristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsAnesStartTimePristine(false);
                    setAnesStartHour(hour);
                    setAnesStartMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* Room In */}
              <div className='ml-2 fx fx__v-center'>
                <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" className='mr-1'>
                    <path fill="#96C8FF" fillRule="evenodd" d="M11.25.804 3 6l8.25 5.196V.804ZM0 12h2V0H0v12Z" clipRule="evenodd" />
                  </svg>
                  Room
                </div>
                <TimePicker
                  className='pl-1 pr-2'
                  hour={inHour}
                  minute={inMinute}
                  color={isInPristine ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isInPristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsInPristine(false);
                    setInHour(hour);
                    setInMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* Surg In */}
              <div className='ml-2 fx fx__v-center'>
                <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" className='mr-1'>
                    <path fill="#96C8FF" fillRule="evenodd" d="M11.25.804 3 6l8.25 5.196V.804ZM0 12h2V0H0v12Z" clipRule="evenodd" />
                  </svg>
                  Surg
                </div>
                <TimePicker
                  className='pl-1 pr-2'
                  hour={surgStartHour}
                  minute={surgStartMinute}
                  // color={isSurgStartPristine ? yellow[500] : blue[500]}
                  color={(isSurgStartPristine || showSurgStartFlag) ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isSurgStartPristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsSurgStartPristine(false);
                    setSurgStartHour(hour);
                    setSurgStartMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* Separator */}
              <div style={{ height: '20px', width: '2px', background: 'black' }} />
              {/* Surg Out */}
              <div className='ml-2 fx fx__v-center'>
                <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" className='mr-1'>
                    <path fill="#96C8FF" fillRule="evenodd" d="M.75 11.196 9 6 .75.804v10.392ZM12 0h-2v12h2V0Z" clipRule="evenodd" />
                  </svg>
                  Surg
                </div>
                <TimePicker
                  className='pl-1 pr-2'
                  hour={surgStopHour}
                  minute={surgStopMinute}
                  // color={isSurgStopPristine ? yellow[500] : blue[500]}
                  color={(isSurgStopPristine || showSurgStopFlag) ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isSurgStopPristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsSurgStopPristine(false);
                    setSurgStopHour(hour);
                    setSurgStopMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* Room Out */}
              <div className='ml-2 fx fx__v-center'>
                <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" className='mr-1'>
                    <path fill="#96C8FF" fillRule="evenodd" d="M.75 11.196 9 6 .75.804v10.392ZM12 0h-2v12h2V0Z" clipRule="evenodd" />
                  </svg>
                  Room
                </div>
                <TimePicker
                  className='pl-1 pr-2'
                  hour={closingHour}
                  minute={closingMinute}
                  // color={isClosingPristine ? yellow[500] : blue[500]}
                  color={(isClosingPristine || showClosingFlag) ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isClosingPristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsClosingPristine(false);
                    setClosingHour(hour);
                    setClosingMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* PACU/ICU */}
              <div
                className='ml-2 fx fx__v-center'
                ref={pacuMenuRef}
                style={{
                  borderRadius: '4px',
                  position: 'relative'
                }}
              >
                <div
                  onClick={() => setShowPacuMenu(true)}
                  className='cursor__pointer'
                  style={{ color: blue[500] }}
                >
                  {patientHandover} <i className={`ml-1 fa ${showPacuMenu ? 'fa-caret-down' : 'fa-caret-up'}`} />
                </div>
                {showPacuMenu &&
                  <PopupMenu
                    top={-48}
                    left={0}
                    zIndex='1550'
                    position="absolute"
                    visible={showPacuMenu}
                    parent={pacuMenuRef}
                    onOutsideClick={() => {
                      setShowPacuMenu(false);
                      setShowPacuMenu(false);
                    }}
                  >
                    <div
                      style={{
                        width: '120px',
                        background: blue[900],
                        paddingBottom: '1px',
                        borderRadius: '5px',
                        overflowY: 'auto',
                        maxHeight: '90px'
                      }}
                    >
                      <div
                        className={`m-item ${patientHandover === 'PACU' ? 'active' : ''}`}
                        style={{ height: '24px' }}
                        onClick={() => {
                          setPatientHandover('PACU');
                          setShowPacuMenu(false);
                        }}
                      >
                        PACU
                      </div>
                      <div
                        className={`m-item ${patientHandover === 'ICU' ? 'active' : ''}`}
                        style={{ height: '24px' }}
                        onClick={() => {
                          setPatientHandover('ICU');
                          setShowPacuMenu(false);
                        }}
                      >
                        ICU
                      </div>
                    </div>
                  </PopupMenu>
                }
                <TimePicker
                  className='pl-2 pr-2'
                  hour={phoHour}
                  minute={phoMinute}
                  // color={isPhoPristine ? yellow[500] : blue[500]}
                  color={(isPhoPristine || showPhoFlag) ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isPhoPristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsPhoPristine(false);
                    setPhoHour(hour);
                    setPhoMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* Anesthesia Stop */}
              <div className='ml-2 fx fx__v-center'>
                <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" className='mr-1'>
                    <path fill="#96C8FF" fillRule="evenodd" d="M.75 11.196 9 6 .75.804v10.392ZM12 0h-2v12h2V0Z" clipRule="evenodd" />
                  </svg>
                  Anes
                </div>
                <TimePicker
                  className='pl-1 pr-2'
                  hour={anesStopHour}
                  minute={anesStopMinute}
                  // color={isAnesStopTimePristine ? yellow[500] : blue[500]}
                  color={(isAnesStopTimePristine || showAnesStopFlag) ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isAnesStopTimePristine}
                  onChangeTime={(hour, min) => {
                    if (!shouldSaveCase) {
                      setShouldSaveCase(true);
                    }
                    setIsAnesStopTimePristine(false);
                    setAnesStopHour(hour);
                    setAnesStopMinute(min);
                  }}
                  disableTimeFormat
                />
              </div>
              {/* Discharge */}
              {patientAdmType === 'OutPt' &&
                <div className='ml-2 fx fx__v-center'>
                  <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                    Discharge
                  </div>
                  <TimePicker
                    className='pl-1 pr-2'
                    hour={dcHour}
                    minute={dcMinute}
                    // color={isDcPristine ? yellow[500] : blue[500]}
                    color={(isDcPristine || showDischargeFlag) ? yellow[500] : blue[500]}
                    noUnderline
                    timeFormat='24H'
                    timePeriod='AM'
                    isPristine={isDcPristine}
                    onChangeTime={(hour, min) => {
                      if (!shouldSaveCase) {
                        setShouldSaveCase(true);
                      }
                      setIsDcPristine(false);
                      setDcHour(hour);
                      setDcMinute(min);
                    }}
                    disableTimeFormat
                  />
                </div>
              }
            </div>
          </div>
          {/* --- Schedule Drawer -------- */}
          <div
            className={css(styles.scheduleFormContainer,
              isMobileLayout && styles.scheduleFormContainerMobile,
              !showScheduleDrawer && styles.scheduleFormContainerClosed
            )}
          >
            {/* Header */}
            <div className={css(styles.scheduleFormHeader)}>
              {/* Header Text */}
              <div className={css(styles.scheduleFormHeaderTitle)}>
                {masterFlag === FLAG_VALUE.success && SUCCESS_ICON}
                {masterFlag === FLAG_VALUE.warning && BELL_ICON}
                {masterFlag === FLAG_VALUE.critical && CRITIAL_ICON}
                <span className='ml-2'>
                  Case Posting
                </span>
              </div>
              {/* Close Icon */}
              <div className={css(styles.scheduleFormHeaderCloseIcon)}>
                <i
                  className='fa fa-times-circle'
                  onClick={closeScheduleCaseDrawer}
                />
              </div>
            </div>
            {/* Schedule Drawer Tabs */}
            <div className={css(styles.scheduleDrawerTabs)}>
              <TabsContainer
                dropDownId="new_case_post_drawer_tabs"
                showPlusIcon={false}
                isDrawer={false}
                shiftMethod='minWidth'
                minWidth={610}
                containerStyles={{ width: '100%' }}
              >
                {/* Case Tab */}
                <TabItem
                  key='1211'
                  tabKey='1211'
                  parentKey='1200'
                  text='Case'
                  isActive={drawerActiveTab === 'case'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={caseTabIcon}
                  style={{
                    ...COMMON_TAB_ITEM_STYLES,
                    width: '105px',
                    minWidth: '105px',
                    maxWidth: '105px',
                    background: drawerActiveTab === 'case' ? '#184878' : '#083050'
                  }}
                  textStyles={TABS_TEXT_STYLES}
                  onActivate={() => {
                    handleOnActivateDrawerTab('case');
                  }}
                >
                  Case
                </TabItem>
                {/* Patient Tab */}
                <TabItem
                  key='1212'
                  tabKey='1212'
                  parentKey='1201'
                  text='Patient'
                  isActive={drawerActiveTab === 'patient'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={patientFlag === FLAG_VALUE.success ? SUCCESS_ICON : BELL_ICON}
                  style={{
                    ...COMMON_TAB_ITEM_STYLES,
                    width: '105px',
                    minWidth: '105px',
                    maxWidth: '105px',
                    background: drawerActiveTab === 'patient' ? '#184878' : '#083050'
                  }}
                  textStyles={TABS_TEXT_STYLES}
                  onActivate={() => {
                    if (caseTabRef.current) {
                      caseTabRef.current.updateCaseData(false);
                    }
                    handleOnActivateDrawerTab('patient');
                  }}
                >
                  Patient
                </TabItem>
                {/* Payers Tab */}
                <TabItem
                  key='1213'
                  tabKey='1214'
                  parentKey='1301'
                  text='Payers'
                  isActive={drawerActiveTab === 'payers'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={payerFlag === FLAG_VALUE.success ? SUCCESS_ICON : BELL_ICON}
                  style={{
                    ...COMMON_TAB_ITEM_STYLES,
                    width: '105px',
                    minWidth: '105px',
                    maxWidth: '105px',
                    background: drawerActiveTab === 'payers' ? '#184878' : '#083050'
                  }}
                  textStyles={TABS_TEXT_STYLES}
                  onActivate={() => {
                    if (caseTabRef.current) {
                      caseTabRef.current.updateCaseData(false);
                    }
                    handleOnActivateDrawerTab('payers');
                  }}
                >
                  Payers
                </TabItem>
              </TabsContainer>
            </div>
            {/* Active Component */}
            <div className={css(styles.activeComponentContainer)}>
              {activeDrawerComponent}
            </div>
          </div>
        </div>
      </div>
    </CaseScheduleContext.Provider>
  );
};

export default CaseDashboard;
